/* Rug Quiz — personalized rug design funnel.
 *
 * Self-boots on any element with [data-rug-quiz]. Per-instance config comes
 * from a child <script type="application/json" data-rug-quiz-config> tag:
 *   { "apiBaseUrl": "https://api.example.com", "mock": true }
 *
 * API contract (homesrug Django backend, /api/ruggen/*):
 *   POST /generate/  {email, style, size, material, shape, colors[], description}
 *     -> 202 {generation_id, status:'pending', poll_url, pricing, generations_remaining}
 *   GET  /<id>/      -> {status, rug_images:[{index, base64_data, mime_type, is_favorite}], pricing}
 *   POST /<id>/favorite/  {index, is_favorite}
 *   POST /checkout/  {generation_id, selected_rug_index, quantity} -> {checkout_url, price}
 */
(function () {
  'use strict';

  var MAX_COLORS = 6;
  var POLL_INTERVAL_MS = 3000;
  var POLL_TIMEOUT_MS = 180000;

  var STYLES = [
    { id: 'Persian', blurb: 'Medallions & ornate borders' },
    { id: 'Moroccan', blurb: 'Trellis lines, lived-in warmth' },
    { id: 'Bohemian', blurb: 'Free-spirited, layered motifs' },
    { id: 'Modern', blurb: 'Clean fields, quiet color' },
    { id: 'Geometric', blurb: 'Crisp repeating shapes' },
    { id: 'Scandinavian', blurb: 'Soft minimalism, light lines' },
    { id: 'Traditional', blurb: 'Classic frames, timeless' },
    { id: 'Contemporary', blurb: 'Painterly, of-the-moment' },
    { id: 'Tribal', blurb: 'Diamonds, zigzags, story' },
    { id: 'Abstract', blurb: 'Organic shapes, pure art' }
  ];

  // Every name here is sent verbatim to the image model as a colour the rug
  // must contain, so each one carries a colour head-noun: bare "gold" comes
  // back as metallic sheen, bare "camel" as an actual camel, bare "rust" as
  // corroded metal. Swatch hexes are pitched for dyed wool (a few points
  // darker and less saturated than the screen colour) so the chip honestly
  // predicts what gets woven. Keep in step with suggested_colors in the
  // backend's views.py — the sanity suite fails if they drift.
  var COLOR_GROUPS = [
    { label: 'Warm neutrals', colors: [
      { id: 'cream', hex: '#f1e9d7' },
      { id: 'sand beige', hex: '#dcc9a4' },
      { id: 'taupe', hex: '#a89383' }
    ] },
    { label: 'Earthy bolds', colors: [
      { id: 'camel brown', hex: '#bb8d5b' },
      { id: 'ochre', hex: '#bd8c22' },
      { id: 'terracotta', hex: '#c46a4a' },
      { id: 'rust orange', hex: '#a94e2b' },
      { id: 'chocolate brown', hex: '#4f382c' }
    ] },
    { label: 'Reds & rose', colors: [
      { id: 'brick red', hex: '#9c2f2a' },
      { id: 'burgundy', hex: '#6e2b3a' },
      { id: 'dusty rose', hex: '#c8918a' }
    ] },
    { label: 'Greens', colors: [
      { id: 'sage green', hex: '#9cab8d' },
      { id: 'olive green', hex: '#6b6b3f' },
      { id: 'forest green', hex: '#37543e' }
    ] },
    { label: 'Blues', colors: [
      { id: 'sky blue', hex: '#8fb0c8' },
      { id: 'teal blue', hex: '#2f6b6b' },
      { id: 'navy blue', hex: '#2e3f66' }
    ] },
    { label: 'Greys & blacks', colors: [
      { id: 'stone grey', hex: '#9d9c97' },
      { id: 'charcoal grey', hex: '#494540' },
      { id: 'black', hex: '#221f1c' }
    ] }
  ];

  var COLORS = COLOR_GROUPS.reduce(function (all, g) { return all.concat(g.colors); }, []);

  // Colours that suit each style, surfaced first on the colour step. Keyed by
  // the LIVE tile title lowercased (merchants rename tiles in the theme
  // editor), with the built-in style ids aliased so both resolve. An unknown
  // style just gets the palette in its normal order.
  var STYLE_COLORS = {
    'heritage & vintage': ['brick red', 'navy blue', 'cream', 'ochre', 'forest green', 'burgundy'],
    'persian': ['brick red', 'navy blue', 'cream', 'ochre', 'forest green', 'burgundy'],
    'bohemian & moroccan': ['cream', 'black', 'terracotta', 'rust orange', 'ochre', 'dusty rose'],
    'bohemian': ['cream', 'black', 'terracotta', 'rust orange', 'ochre', 'dusty rose'],
    'moroccan': ['cream', 'black', 'terracotta', 'rust orange', 'ochre', 'dusty rose'],
    'modern & contemporary': ['cream', 'charcoal grey', 'stone grey', 'black', 'taupe', 'teal blue'],
    'modern': ['cream', 'charcoal grey', 'stone grey', 'black', 'taupe', 'teal blue'],
    'contemporary': ['cream', 'charcoal grey', 'stone grey', 'black', 'taupe', 'teal blue'],
    'organic modern': ['cream', 'sand beige', 'taupe', 'chocolate brown', 'olive green', 'terracotta'],
    'transitional': ['cream', 'stone grey', 'navy blue', 'sky blue', 'camel brown', 'rust orange'],
    'traditional': ['cream', 'stone grey', 'navy blue', 'sky blue', 'camel brown', 'rust orange'],
    'coastal': ['cream', 'sky blue', 'navy blue', 'sage green', 'sand beige', 'stone grey'],
    'geometric': ['charcoal grey', 'cream', 'navy blue', 'ochre', 'terracotta', 'teal blue'],
    'scandinavian': ['cream', 'stone grey', 'charcoal grey', 'sage green', 'dusty rose', 'sky blue'],
    'tribal': ['black', 'cream', 'brick red', 'ochre', 'navy blue', 'chocolate brown'],
    'abstract': ['cream', 'charcoal grey', 'terracotta', 'teal blue', 'dusty rose', 'olive green']
  };

  function styleColorsFor(style) {
    return STYLE_COLORS[String(style || '').trim().toLowerCase()] || null;
  }

  // What each construction can physically weave. `best` sorts a material to
  // the front for those styles; `poor` is reserved for pairs the craft truly
  // cannot make — the pile materials can render anything, so only the flat
  // weaves carry warnings. Nothing is ever hidden: a shopper who wants a plain
  // jute rug in a heritage-styled room can still order one, they just get told
  // what to expect.
  var MATERIAL_FIT = {
    'Jute': {
      best: ['coastal', 'organic modern', 'scandinavian', 'transitional', 'bohemian & moroccan', 'tribal'],
      poor: {
        'heritage & vintage': 'Jute is too coarse for medallion and floral detail — expect a simplified, banded rug.',
        'persian': 'Jute is too coarse for medallion and floral detail — expect a simplified, banded rug.',
        'modern & contemporary': 'Jute can’t hold painterly blends — expect flat, natural tones.',
        'contemporary': 'Jute can’t hold painterly blends — expect flat, natural tones.',
        'abstract': 'Jute can’t hold soft gradation — expect bold, simple shapes.'
      }
    },
    'Cotton': {
      best: ['geometric', 'tribal', 'scandinavian', 'bohemian & moroccan', 'coastal', 'transitional'],
      poor: {
        'heritage & vintage': 'A flat weave can’t hold ornate medallion work — expect a simplified, geometric take.',
        'persian': 'A flat weave can’t hold ornate medallion work — expect a simplified, geometric take.',
        'modern & contemporary': 'Flat weaving makes crisp blocks, not gradients — expect harder edges.',
        'contemporary': 'Flat weaving makes crisp blocks, not gradients — expect harder edges.',
        'organic modern': 'Curved organic forms are hard on a flat loom — expect straighter edges.'
      }
    },
    'Hand-Knotted New Zealand Wool': { best: ['heritage & vintage', 'persian', 'transitional', 'traditional', 'tribal', 'geometric'], poor: {} },
    'Hand-Knotted Silk': { best: ['heritage & vintage', 'persian', 'modern & contemporary', 'contemporary', 'abstract'], poor: {} },
    'Hand-Knotted Viscose': { best: ['modern & contemporary', 'contemporary', 'abstract', 'transitional'], poor: {} },
    'Hand-Tufted New Zealand Wool': { best: ['bohemian & moroccan', 'moroccan', 'organic modern', 'modern & contemporary', 'contemporary', 'geometric', 'scandinavian', 'abstract'], poor: {} },
    'Hand-Tufted Viscose': { best: ['modern & contemporary', 'contemporary', 'abstract', 'organic modern'], poor: {} }
  };

  function materialFitFor(materialId, style) {
    var fit = MATERIAL_FIT[materialId];
    var key = String(style || '').trim().toLowerCase();
    if (!fit) return { recommended: false, warning: null };
    return {
      recommended: fit.best.indexOf(key) !== -1,
      warning: fit.poor[key] || null
    };
  }

  // Copy only — every rate comes from the backend (see PRICING). Money must
  // never be hardcoded here: the storefront is editable by anyone, and a
  // stale number here would quote a price checkout won't honour.
  var MATERIALS = [
    { id: 'Hand-Tufted New Zealand Wool', blurb: 'Plush, springy wool — our signature', tag: 'Most loved' },
    { id: 'Hand-Tufted Viscose', blurb: 'Silky sheen, soft underfoot', tag: '' },
    { id: 'Hand-Knotted New Zealand Wool', blurb: 'Heirloom quality, knot by knot', tag: 'Heirloom' },
    { id: 'Hand-Knotted Viscose', blurb: 'Luminous drape, fine detail', tag: '' },
    { id: 'Hand-Knotted Silk', blurb: 'Pure silk, knotted by master weavers', tag: 'Rarest' },
    { id: 'Jute', blurb: 'Earthy, natural woven texture', tag: 'Natural' },
    { id: 'Cotton', blurb: 'Light, washable, easy living', tag: 'Easy care' }
  ];

  var SIZE_PRESETS = {
    rectangular: ['3x5 feet', '4x6 feet', '5x8 feet', '6x9 feet', '8x10 feet', '9x12 feet'],
    round: ['4x4 feet', '5x5 feet', '6x6 feet', '8x8 feet'],
    oval: ['3x5 feet', '4x6 feet', '5x8 feet', '6x9 feet', '8x10 feet'],
    irregular: ['3x5 feet', '4x6 feet', '5x8 feet', '6x9 feet', '8x10 feet'],
    animal: ['3x5 feet', '4x6 feet', '5x8 feet', '6x9 feet']
  };

  var GENERATING_LINES = [
    'Reading your style notes…',
    'Mixing your palette…',
    'Sketching pattern directions…',
    'Weaving design 1 of 4…',
    'Weaving design 2 of 4…',
    'Weaving design 3 of 4…',
    'Weaving design 4 of 4…',
    'Adding the finishing touches…'
  ];

  // Deterministic demo names so the mock flow shows the same UX as live.
  var MOCK_NAME_A = ['Ember', 'Indigo', 'Sage', 'Canyon', 'Dune', 'Juniper', 'Terra', 'Moss', 'Amber', 'Cedar', 'Onyx', 'Ivory'];
  var MOCK_NAME_B = ['Trellis', 'Drift', 'Meadow', 'Path', 'Field', 'Mesa', 'Garden', 'Loom', 'Haven', 'Ridge', 'Bloom', 'Tide'];
  function mockDesignName(colors, seed) {
    var a = MOCK_NAME_A[(seed * 7 + (colors && colors.length ? colors[0].length : 0)) % MOCK_NAME_A.length];
    var b = MOCK_NAME_B[(seed * 13) % MOCK_NAME_B.length];
    return a + ' ' + b;
  }

  // Pricing is published by the backend at /options/ and cached here — the
  // theme holds no rates of its own. Until it loads (or if it fails) every
  // price falls back to "Price at checkout" rather than guessing a number.
  // The backend recomputes the real price on every checkout regardless, so
  // tampering with this only changes what the shopper is shown.
  var PRICING = null; // { rates: {key: rate}, defaultRate, curve: {...} }

  function applyPricingConfig(pricing) {
    if (!pricing || !pricing.rates) return false;
    var curve = pricing.size_curve || {};
    PRICING = {
      rates: pricing.rates,
      defaultRate: pricing.default_rate || null,
      currency: pricing.currency || 'USD',
      curve: {
        anchorSqft: curve.anchor_sqft || 80,
        exponent: curve.exponent != null ? curve.exponent : 0,
        minMultiplier: curve.min_multiplier != null ? curve.min_multiplier : 1,
        maxMultiplier: curve.max_multiplier != null ? curve.max_multiplier : 1
      }
    };
    return true;
  }

  function materialKey(material) {
    return String(material || '').toLowerCase().replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
  }

  // Anchor-size rate for a material, or null when pricing hasn't loaded.
  function baseRateFor(material) {
    if (!PRICING) return null;
    var r = PRICING.rates[materialKey(material)];
    return typeof r === 'number' ? r : null;
  }

  // Smaller rugs carry more of the fixed shipping/handling cost per square
  // foot. Mirrors pricing.size_multiplier() on the backend.
  function sizeMultiplier(sqft) {
    if (!PRICING || !sqft || sqft <= 0) return 1;
    var c = PRICING.curve;
    var m = Math.pow(c.anchorSqft / sqft, c.exponent);
    return Math.min(Math.max(m, c.minMultiplier), c.maxMultiplier);
  }

  // What this rug actually pays per square foot at this size.
  function effectiveRateFor(material, size, shape) {
    var base = baseRateFor(material);
    var sqft = sqftExact(size, shape);
    if (!base || !sqft) return null;
    return base * sizeMultiplier(sqft);
  }

  /* ---------- small utilities ---------- */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function mulberry32(seed) {
    var a = seed | 0;
    return function () {
      a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  var IDENTITY = { customer_id: null };

  // Called once per page when a logged-in customer is present: stamps the
  // GA4 user_id, and pushes identify events (customer id + SHA-256 email —
  // never the raw address) for GTM / Meta advanced matching.
  function identify(config) {
    if (!config || !config.customerId) return;
    IDENTITY.customer_id = String(config.customerId);
    try {
      (window.dataLayer = window.dataLayer || []).push({ event: 'rug_quiz_identify', customer_id: IDENTITY.customer_id });
      if (typeof window.gtag === 'function') window.gtag('set', 'user_id', IDENTITY.customer_id);
      var email = config.customerEmail;
      if (email && window.crypto && window.crypto.subtle) {
        window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(String(email).trim().toLowerCase()))
          .then(function (buf) {
            var hex = Array.prototype.map.call(new Uint8Array(buf), function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
            window.dataLayer.push({ event: 'rug_quiz_identify_em', sha256_email: hex, customer_id: IDENTITY.customer_id });
          }).catch(function () {});
      }
    } catch (e) { /* analytics must never break the funnel */ }
  }

  function track(event, props) {
    props = Object.assign({}, props || {});
    if (IDENTITY.customer_id) props.customer_id = IDENTITY.customer_id;
    var detail = Object.assign({ event: 'rug_quiz_' + event }, props);
    try {
      document.dispatchEvent(new CustomEvent('rugquiz:track', { detail: detail }));
      (window.dataLayer = window.dataLayer || []).push(detail);
      // GA4 (direct gtag installs — GTM/dataLayer covered above)
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'rug_quiz_' + event, props || {});
      }
      // Meta pixel: custom funnel events + standard events for ad optimization
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'RugQuiz_' + event, props);
        if (event === 'generation_requested') window.fbq('track', 'Lead');
        if (event === 'checkout_started') window.fbq('track', 'InitiateCheckout');
      }
    } catch (e) { /* analytics must never break the funnel */ }
  }

  function parseSize(text) {
    var m = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*(feet|ft|cm)?/i.exec(text || '');
    if (!m) return null;
    var w = parseFloat(m[1]);
    var h = parseFloat(m[2]);
    var unit = (m[3] || 'feet').toLowerCase();
    if (unit === 'cm') { w /= 30.48; h /= 30.48; }
    return { wFt: w, hFt: h };
  }

  // The UI promises "Minimum 2x2 feet (61x61 cm)" — enforce it. Round rugs
  // take a single diameter ("6 feet", "180 cm"), mirroring the backend's
  // parser; a WxH entry with two different numbers is rejected rather than
  // silently priced off the first one.
  function parseCustomSize(text, shape) {
    if (shape === 'round') {
      var m = /^\s*(\d+(?:\.\d+)?)\s*(?:[x×]\s*(\d+(?:\.\d+)?)\s*)?(feet|ft|cm)?\s*$/i.exec(text || '');
      if (!m) return null;
      var d = parseFloat(m[1]);
      if (m[2] != null && parseFloat(m[2]) !== d) return { roundMismatch: true };
      if ((m[3] || 'feet').toLowerCase() === 'cm') d /= 30.48;
      return { wFt: d, hFt: d };
    }
    return parseSize(text);
  }

  function customSizeOk(text, shape) {
    var p = parseCustomSize(text, shape);
    return !!p && !p.roundMismatch && p.wFt >= 2 && p.hFt >= 2;
  }

  function customSizeError(text, shape) {
    var p = parseCustomSize(text, shape);
    if (shape === 'round') {
      if (p && p.roundMismatch) return 'Round rugs take one diameter — e.g. 6 feet';
      if (!p) return 'Enter a diameter like 6 feet or 180 cm';
      return 'Minimum diameter is 2 feet (61 cm)';
    }
    return p ? 'Minimum size is 2x2 feet' : 'Enter a size like 7x11 feet';
  }

  function customSizePlaceholder(shape) {
    return shape === 'round' ? 'e.g. 6 feet across or 180 cm' : 'e.g. 7x11 feet or 200x300 cm';
  }

  function fmtMoney(amount, currency) {
    if (amount == null || isNaN(amount)) return '';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount);
    } catch (e) {
      return '$' + Number(amount).toFixed(2);
    }
  }

  /* ---------- decorative rug SVGs (style tiles + demo-mode designs) ---------- */

  function svgDataUri(svg) {
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
  }

  // Placeholder art colours for a style, derived from the same per-style sets
  // the colour step recommends — so the demo art, the style tiles and the
  // recommended swatches all agree. Keyed case-insensitively off the live
  // tile title, which merchants rename (the old map was keyed on the built-in
  // ids and silently fell through to Abstract for every renamed tile).
  function styleDefaultPalette(styleId) {
    var names = styleColorsFor(styleId);
    if (names) {
      var hexes = names.map(colorHex).filter(Boolean);
      if (hexes.length >= 4) return hexes;
    }
    return ['#f1e9d7', '#494540', '#c46a4a', '#2e3f66'];
  }

  function colorById(name) {
    for (var i = 0; i < COLORS.length; i++) {
      if (COLORS[i].id === name) return COLORS[i];
    }
    return null;
  }

  function colorHex(name) {
    var c = colorById(name);
    return c ? c.hex : null;
  }

  // Best-effort hex for free-typed color names; falls back to a warm neutral.
  function guessHex(name) {
    var known = colorHex(name);
    if (known) return known;
    var probe = document.createElement('span');
    probe.style.color = '';
    probe.style.color = String(name).replace(/\s+/g, '');
    if (probe.style.color) return probe.style.color;
    return '#b5a894';
  }

  function patternElements(styleId, W, H, p, rnd) {
    var out = [];
    var i, x, y;
    function rect(x, y, w, h, fill, extra) {
      out.push('<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" fill="' + fill + '"' + (extra || '') + '/>');
    }
    function circle(cx, cy, r, fill, extra) {
      out.push('<circle cx="' + cx + '" cy="' + cy + '" r="' + r + '" fill="' + fill + '"' + (extra || '') + '/>');
    }
    function diamond(cx, cy, r, fill) {
      out.push('<path d="M' + cx + ' ' + (cy - r) + 'L' + (cx + r) + ' ' + cy + 'L' + cx + ' ' + (cy + r) + 'L' + (cx - r) + ' ' + cy + 'Z" fill="' + fill + '"/>');
    }

    if (styleId === 'Persian' || styleId === 'Traditional') {
      diamond(W / 2, H / 2, Math.min(W, H) * 0.30, p(2));
      diamond(W / 2, H / 2, Math.min(W, H) * 0.22, p(3));
      diamond(W / 2, H / 2, Math.min(W, H) * 0.12, p(1));
      circle(W / 2, H / 2, Math.min(W, H) * 0.05, p(2));
      var cr = Math.min(W, H) * 0.16;
      diamond(0, 0, cr, p(3)); diamond(W, 0, cr, p(3));
      diamond(0, H, cr, p(3)); diamond(W, H, cr, p(3));
    } else if (styleId === 'Moroccan') {
      var step = W / 5;
      for (x = 0; x <= 5; x++) {
        for (y = 0; y <= Math.ceil(H / step); y++) {
          var ox = x * step + (y % 2 ? step / 2 : 0);
          out.push('<path d="M' + ox + ' ' + (y * step) + ' q ' + (step / 2) + ' ' + (step / 2) + ' 0 ' + step + ' q ' + (-step / 2) + ' ' + (-step / 2) + ' 0 ' + (-step) + 'Z" fill="none" stroke="' + p(1) + '" stroke-width="2.5"/>');
        }
      }
    } else if (styleId === 'Bohemian') {
      for (i = 0; i < 14; i++) {
        var bx = rnd() * W, by = rnd() * H, br = 8 + rnd() * 26;
        circle(bx, by, br, 'none', ' stroke="' + p(1 + (i % 3)) + '" stroke-width="3"');
        if (i % 3 === 0) circle(bx, by, br * 0.4, p(1 + ((i + 1) % 3)));
      }
    } else if (styleId === 'Modern') {
      rect(0, H * 0.62, W, H * 0.38, p(1));
      rect(W * 0.12, H * 0.2, W * 0.30, H * 0.42, p(2));
      circle(W * 0.72, H * 0.34, Math.min(W, H) * 0.14, p(3));
    } else if (styleId === 'Geometric') {
      var gs = W / 6;
      for (x = 0; x < 6; x++) {
        for (y = 0; y < Math.ceil(H / gs); y++) {
          var gx = x * gs, gy = y * gs, c = p(1 + ((x + y) % 3));
          out.push('<path d="M' + gx + ' ' + (gy + gs) + 'L' + (gx + gs / 2) + ' ' + gy + 'L' + (gx + gs) + ' ' + (gy + gs) + 'Z" fill="' + c + '" opacity="0.9"/>');
        }
      }
    } else if (styleId === 'Scandinavian') {
      for (y = 0; y < Math.ceil(H / 26); y++) {
        out.push('<path d="M0 ' + (y * 26 + 13) + ' l 13 -8 l 13 8 l 13 -8 l 13 8 l 13 -8 l 13 8 l 13 -8 l 13 8 l 13 -8 l 13 8 l 13 -8 l 13 8" fill="none" stroke="' + p(1 + (y % 3)) + '" stroke-width="2"/>');
      }
    } else if (styleId === 'Tribal') {
      for (y = 0; y < Math.ceil(H / 40); y++) {
        out.push('<path d="M0 ' + (y * 40 + 20) + ' l 20 -14 l 20 14 l 20 -14 l 20 14 l 20 -14 l 20 14 l 20 -14 l 20 14 l 20 -14 l 20 14" fill="none" stroke="' + p(1 + (y % 3)) + '" stroke-width="4"/>');
        diamond(W * (0.25 + 0.5 * (y % 2)), y * 40 + 20, 9, p(1 + ((y + 1) % 3)));
      }
    } else if (styleId === 'Contemporary') {
      out.push('<path d="M0 ' + H * 0.7 + ' C ' + W * 0.3 + ' ' + H * 0.45 + ', ' + W * 0.6 + ' ' + H * 0.95 + ', ' + W + ' ' + H * 0.6 + ' L ' + W + ' ' + H + ' L 0 ' + H + ' Z" fill="' + p(1) + '"/>');
      out.push('<path d="M0 ' + H * 0.35 + ' C ' + W * 0.4 + ' ' + H * 0.15 + ', ' + W * 0.55 + ' ' + H * 0.5 + ', ' + W + ' ' + H * 0.25 + '" fill="none" stroke="' + p(2) + '" stroke-width="6" stroke-linecap="round"/>');
      circle(W * 0.78, H * 0.22, Math.min(W, H) * 0.09, p(3));
    } else { // Abstract & fallback
      for (i = 0; i < 6; i++) {
        var ax = rnd() * W, ay = rnd() * H, ar = 14 + rnd() * 40;
        out.push('<ellipse cx="' + ax + '" cy="' + ay + '" rx="' + ar + '" ry="' + ar * (0.5 + rnd() * 0.8) + '" fill="' + p(1 + (i % 3)) + '" opacity="0.8" transform="rotate(' + Math.round(rnd() * 90) + ' ' + ax + ' ' + ay + ')"/>');
      }
    }
    return out.join('');
  }

  function rugSVG(opts) {
    var styleId = opts.style || 'Abstract';
    // Honour every colour the shopper picked (up to MAX_COLORS) — slicing to
    // four silently dropped picks 5 and 6 from the preview art.
    var palette = (opts.colors && opts.colors.length ? opts.colors.map(guessHex) : styleDefaultPalette(styleId)).slice(0, MAX_COLORS);
    var fallback = styleDefaultPalette(styleId);
    while (palette.length < 4) palette.push(fallback[palette.length % fallback.length]);
    if (opts.seed) {
      palette = palette.slice(opts.seed % palette.length).concat(palette.slice(0, opts.seed % palette.length));
    }
    var p = function (i) { return palette[i % palette.length]; };
    var rnd = mulberry32((opts.seed || 1) * 7919 + styleId.length);

    var round = opts.shape === 'round';
    var W = 300;
    var ratio = 1;
    if (!round && opts.size) {
      var parsed = parseSize(opts.size);
      if (parsed) ratio = Math.max(0.45, Math.min(1.6, parsed.hFt / parsed.wFt));
    }
    var H = Math.round(W * (round ? 1 : ratio));
    var inset = 18;
    var clipId = 'rc' + (opts.seed || 0) + styleId.length;

    // width/height attributes are required for canvas drawImage of SVG in Firefox
    var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + W + '" height="' + H + '" viewBox="0 0 ' + W + ' ' + H + '">';
    if (round) {
      svg += '<defs><clipPath id="' + clipId + '"><circle cx="' + W / 2 + '" cy="' + H / 2 + '" r="' + (W / 2 - 4) + '"/></clipPath></defs>';
      svg += '<g clip-path="url(#' + clipId + ')">';
      svg += '<rect width="' + W + '" height="' + H + '" fill="' + p(0) + '"/>';
      svg += patternElements(styleId, W, H, p, rnd);
      svg += '</g>';
      svg += '<circle cx="' + W / 2 + '" cy="' + H / 2 + '" r="' + (W / 2 - 6) + '" fill="none" stroke="' + p(1) + '" stroke-width="6"/>';
      svg += '<circle cx="' + W / 2 + '" cy="' + H / 2 + '" r="' + (W / 2 - 14) + '" fill="none" stroke="' + p(2) + '" stroke-width="2"/>';
    } else {
      svg += '<rect width="' + W + '" height="' + H + '" fill="' + p(1) + '"/>';
      svg += '<rect x="' + (inset - 8) + '" y="' + (inset - 8) + '" width="' + (W - inset * 2 + 16) + '" height="' + (H - inset * 2 + 16) + '" fill="none" stroke="' + p(2) + '" stroke-width="3"/>';
      svg += '<g transform="translate(' + inset + ',' + inset + ')">';
      svg += '<rect width="' + (W - inset * 2) + '" height="' + (H - inset * 2) + '" fill="' + p(0) + '"/>';
      svg += '<defs><clipPath id="' + clipId + '"><rect width="' + (W - inset * 2) + '" height="' + (H - inset * 2) + '"/></clipPath></defs>';
      svg += '<g clip-path="url(#' + clipId + ')">' + patternElements(styleId, W - inset * 2, H - inset * 2, p, rnd) + '</g>';
      svg += '</g>';
    }
    svg += '</svg>';
    return svgDataUri(svg);
  }

  /* ---------- room photo analysis (client-side, no AI needed) ---------- */

  function hexToRgb(hex) {
    var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
    return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : { r: 0, g: 0, b: 0 };
  }

  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var l = (max + min) / 2;
    var s = max === min ? 0 : (max - min) / (1 - Math.abs(2 * l - 1));
    var h = 0;
    if (max !== min) {
      if (max === r) h = ((g - b) / (max - min)) % 6;
      else if (max === g) h = (b - r) / (max - min) + 2;
      else h = (r - g) / (max - min) + 4;
      h = (h * 60 + 360) % 360;
    }
    return { h: h, s: s, l: l };
  }

  function nearestColorName(r, g, b) {
    var best = COLORS[0].id, bestDist = Infinity;
    for (var i = 0; i < COLORS.length; i++) {
      var c = hexToRgb(COLORS[i].hex);
      var d = (c.r - r) * (c.r - r) + (c.g - g) * (c.g - g) + (c.b - b) * (c.b - b);
      if (d < bestDist) { bestDist = d; best = COLORS[i].id; }
    }
    return best;
  }

  function suggestStyleFromStats(buckets) {
    var n = 0, s = 0, l = 0, warm = 0;
    buckets.forEach(function (bk) {
      var hsl = rgbToHsl(bk.r, bk.g, bk.b);
      n += bk.n; s += hsl.s * bk.n; l += hsl.l * bk.n;
      if (hsl.h < 70 || hsl.h > 330) warm += bk.n;
    });
    s /= n || 1; l /= n || 1; warm /= n || 1;
    if (l > 0.72 && s < 0.28) return 'Scandinavian';
    if (s < 0.16) return 'Modern';
    if (l < 0.32) return 'Traditional';
    if (warm > 0.6) return 'Bohemian';
    return 'Contemporary';
  }

  // Customer design uploads keep their resolution (it IS the print quality),
  // capped at 3000px on the long edge to stay well inside upload limits.
  function processDesignFile(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () {
        var img = new Image();
        img.onload = function () {
          var w = img.naturalWidth;
          var h = img.naturalHeight;
          var scale = Math.min(1, 3000 / Math.max(w, h));
          var cw = Math.round(w * scale);
          var ch = Math.round(h * scale);
          var canvas = document.createElement('canvas');
          canvas.width = cw;
          canvas.height = ch;
          canvas.getContext('2d').drawImage(img, 0, 0, cw, ch);
          resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.92), px: { w: cw, h: ch } });
        };
        img.onerror = function () { reject(new Error('We couldn’t read that image — please try another.')); };
        img.src = reader.result;
      };
      reader.onerror = function () { reject(new Error('We couldn’t read that file — please try another.')); };
      reader.readAsDataURL(file);
    });
  }

  // Downscales the photo for upload, extracts a dominant palette, and
  // suggests a style. Everything runs locally in the browser.
  function analyzeRoomPhoto(file) {
    return new Promise(function (resolve, reject) {
      if (!/^image\//.test(file.type)) {
        return reject(new Error('Please choose a photo (JPG or PNG).'));
      }
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        try {
          var maxW = 1280;
          var scale = Math.min(1, maxW / img.naturalWidth);
          var w = Math.max(1, Math.round(img.naturalWidth * scale));
          var h = Math.max(1, Math.round(img.naturalHeight * scale));
          var canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          var dataUrl = canvas.toDataURL('image/jpeg', 0.82);

          var sw = 48, sh = 36;
          var sample = document.createElement('canvas');
          sample.width = sw; sample.height = sh;
          var sctx = sample.getContext('2d');
          sctx.drawImage(img, 0, 0, sw, sh);
          var data = sctx.getImageData(0, 0, sw, sh).data;
          var buckets = {};
          for (var i = 0; i < data.length; i += 4) {
            var key = (data[i] >> 5) + ',' + (data[i + 1] >> 5) + ',' + (data[i + 2] >> 5);
            var bk = buckets[key] || (buckets[key] = { n: 0, rSum: 0, gSum: 0, bSum: 0 });
            bk.n++; bk.rSum += data[i]; bk.gSum += data[i + 1]; bk.bSum += data[i + 2];
          }
          var top = Object.keys(buckets).map(function (k) {
            var bk = buckets[k];
            return { n: bk.n, r: Math.round(bk.rSum / bk.n), g: Math.round(bk.gSum / bk.n), b: Math.round(bk.bSum / bk.n) };
          }).sort(function (a, b) { return b.n - a.n; }).slice(0, 8);

          var palette = [];
          top.forEach(function (bk) {
            var name = nearestColorName(bk.r, bk.g, bk.b);
            if (palette.length < 3 && !palette.some(function (p) { return p.name === name; })) {
              palette.push({ name: name, hex: 'rgb(' + bk.r + ',' + bk.g + ',' + bk.b + ')' });
            }
          });

          resolve({ dataUrl: dataUrl, palette: palette, style: suggestStyleFromStats(top) });
        } catch (e) {
          reject(new Error('We couldn’t read that image — try a JPG or PNG.'));
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('We couldn’t read that image — try a JPG or PNG.'));
      };
      img.src = url;
    });
  }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('Image failed to load')); };
      img.src = src;
    });
  }

  // Demo-mode stand-in for the backend's AI room placement: draws the rug
  // onto the room photo with a fake floor perspective and a soft shadow.
  function composeRoomPreview(roomSrc, rugSrc) {
    return Promise.all([loadImage(roomSrc), loadImage(rugSrc)]).then(function (imgs) {
      var room = imgs[0], rug = imgs[1];
      var canvas = document.createElement('canvas');
      canvas.width = room.naturalWidth;
      canvas.height = room.naturalHeight;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(room, 0, 0);

      // Fake floor perspective: squash the rug vertically and keep it in the
      // lower quarter of the frame so it reads as lying on the floor.
      var rw = canvas.width * 0.56;
      var rh = Math.min(rw * (rug.naturalHeight / rug.naturalWidth) * 0.30, canvas.height * 0.24);
      var rx = (canvas.width - rw) / 2;
      var ry = canvas.height * 0.96 - rh;

      ctx.save();
      ctx.shadowColor = 'rgba(30,25,20,0.45)';
      ctx.shadowBlur = canvas.width * 0.02;
      ctx.fillStyle = 'rgba(30,25,20,0.25)';
      ctx.beginPath();
      ctx.ellipse(rx + rw / 2, ry + rh * 0.95, rw * 0.52, rh * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      ctx.drawImage(rug, rx, ry, rw, rh);
      return canvas.toDataURL('image/jpeg', 0.85);
    });
  }

  /* ---------- API adapters ----------
   * Both adapters resolve the same normalized shapes so the UI never
   * branches on transport:
   *   generate() -> { generationId, pricing, generationsRemaining }
   *   poll()     -> { status, images:[{index, src, isFavorite}], pricing }
   *   checkout() -> { checkoutUrl, price, currency }
   */

  function HttpRugApi(baseUrl, proxyPath) {
    this.base = String(baseUrl || '').replace(/\/+$/, '');
    // On the storefront, calls go through the Shopify App Proxy: same-origin
    // and cryptographically signed, so the backend can trust the logged-in
    // customer id. Off-storefront (dev harness) falls back to the direct API.
    this.proxy = String(proxyPath || '').replace(/\/+$/, '');
  }

  HttpRugApi.prototype.urlFor = function (path) {
    return this.proxy ? this.proxy + path : this.base + '/api/ruggen' + path;
  };

  HttpRugApi.prototype._fetch = function (path, opts) {
    return fetch(this.urlFor(path), opts).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (body) {
        if (!res.ok) {
          var err = new Error(body.message || body.error || ('Request failed (' + res.status + ')'));
          err.code = body.error || res.status;
          err.body = body;
          throw err;
        }
        return body;
      });
    });
  };

  HttpRugApi.prototype.options = function () {
    return this._fetch('/options/', { method: 'GET' });
  };

  HttpRugApi.prototype.generate = function (payload) {
    return this._fetch('/generate/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (body) {
      return {
        generationId: body.generation_id,
        pricing: body.pricing || null,
        generationsRemaining: body.generations_remaining
      };
    });
  };

  HttpRugApi.prototype.uploadDesign = function (payload) {
    var self = this;
    return this._fetch('/upload-design/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (body) {
      return {
        generationId: body.generation_id,
        pricing: body.pricing || null,
        name: body.name || '',
        src: self.urlFor(String(body.image_url || '').replace(/^\/api\/ruggen/, ''))
      };
    });
  };

  HttpRugApi.prototype.poll = function (generationId) {
    var self = this;
    return this._fetch('/' + generationId + '/', { method: 'GET' }).then(function (body) {
      var images = (body.rug_images || []).map(function (img) {
        // S3-primary backend sends a stable image URL; base64 is the
        // fallback while images still live in the database.
        var src = img.image_url
          ? self.urlFor(img.image_url.replace(/^\/api\/ruggen/, ''))
          : 'data:' + (img.mime_type || 'image/png') + ';base64,' + img.base64_data;
        return { index: img.index, src: src, isFavorite: !!img.is_favorite, name: img.name || '' };
      });
      return { status: body.status, images: images, pricing: body.pricing || null };
    });
  };

  HttpRugApi.prototype.favorite = function (generationId, index, isFavorite) {
    return this._fetch('/' + generationId + '/favorite/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ index: index, is_favorite: isFavorite })
    });
  };

  // The room photo is NOT re-sent here: /generate/ already stored it and
  // the backend falls back to that copy, keeping this request small.
  HttpRugApi.prototype.placeInRoom = function (generationId, index, roomDataUrl) {
    return this._fetch('/place/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        generation_id: generationId,
        selected_rug_index: index
      })
    }).then(function (body) {
      return { src: 'data:' + (body.mime_type || 'image/jpeg') + ';base64,' + body.result_base64 };
    });
  };

  HttpRugApi.prototype.checkout = function (generationId, selectedIndex, size) {
    var payload = { generation_id: generationId, selected_rug_index: selectedIndex, quantity: 1 };
    if (size) payload.size = size;
    return this._fetch('/checkout/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (body) {
      return { checkoutUrl: body.checkout_url, variantId: body.variant_id || null, price: body.price, currency: body.currency, size: body.size };
    });
  };

  function MockRugApi() {
    this._generations = {};
    this._quotaUsed = 0;
  }

  MockRugApi.prototype.mockPricing = function (answers) {
    return {
      price: priceFor(answers.material, answers.size, answers.shape),
      currency: 'USD',
      sqft: sqftFor(answers.size, answers.shape),
      material: answers.material,
      shape: answers.shape
    };
  };

  MockRugApi.prototype.generate = function (payload) {
    this._quotaUsed += 1;
    var id = 'demo-' + this._quotaUsed + '-' + payload.style.toLowerCase();
    var images = [];
    for (var i = 0; i < 4; i++) {
      images.push({
        index: i,
        src: rugSVG({ style: payload.style, colors: payload.colors, shape: payload.shape, size: payload.size, seed: this._quotaUsed * 10 + i + 1 }),
        isFavorite: false,
        name: mockDesignName(payload.colors, this._quotaUsed * 10 + i + 1)
      });
    }
    this._generations[id] = {
      readyAt: Date.now() + 6500,
      images: images,
      pricing: this.mockPricing(payload)
    };
    return Promise.resolve({
      generationId: id,
      pricing: this._generations[id].pricing,
      generationsRemaining: Math.max(0, 5 - this._quotaUsed)
    });
  };

  MockRugApi.prototype.uploadDesign = function (payload) {
    this._uploads = (this._uploads || 0) + 1;
    var id = 'demo-upload-' + this._uploads;
    var pricing = this.mockPricing({ size: payload.size, shape: payload.shape, material: payload.material });
    this._generations[id] = {
      readyAt: 0,
      images: [{ index: 0, src: payload.design_image_base64, isFavorite: false, name: 'Your Design' }],
      pricing: pricing
    };
    return Promise.resolve({ generationId: id, pricing: pricing, name: 'Your Design', src: payload.design_image_base64 });
  };

  MockRugApi.prototype.poll = function (generationId) {
    var gen = this._generations[generationId];
    if (!gen) return Promise.reject(new Error('Generation not found'));
    if (Date.now() < gen.readyAt) {
      return Promise.resolve({ status: 'pending', images: [], pricing: gen.pricing });
    }
    return Promise.resolve({ status: 'generated', images: gen.images, pricing: gen.pricing });
  };

  MockRugApi.prototype.favorite = function (generationId, index, isFavorite) {
    var gen = this._generations[generationId];
    if (gen && gen.images[index]) gen.images[index].isFavorite = isFavorite;
    return Promise.resolve({});
  };

  MockRugApi.prototype.placeInRoom = function (generationId, index, roomDataUrl) {
    var gen = this._generations[generationId];
    if (!gen || !gen.images[index]) return Promise.reject(new Error('Generation not found'));
    return composeRoomPreview(roomDataUrl, gen.images[index].src).then(function (src) {
      return new Promise(function (resolve) {
        setTimeout(function () { resolve({ src: src }); }, 900);
      });
    });
  };

  MockRugApi.prototype.checkout = function (generationId, selectedIndex, size) {
    var gen = this._generations[generationId];
    var price = gen ? gen.pricing.price : null;
    if (gen && size) price = priceFor(gen.pricing.material, size, gen.pricing.shape) || price;
    return Promise.resolve({
      checkoutUrl: '',
      price: price,
      currency: 'USD',
      mock: true,
      selectedIndex: selectedIndex,
      size: size || (gen ? gen.pricing.size : null)
    });
  };

  /* ---------- quiz application ---------- */

  var STEP_ORDER = ['photo', 'style', 'colors', 'shape', 'size', 'material', 'details', 'email'];

  // "Order your own design" path: customer-supplied artwork, no AI — a
  // shorter flow that skips style/colors/description entirely.
  // No shape step: an uploaded design is produced in the artwork's own shape,
  // so we only need its width x length (priced by bounding box).
  var UPLOAD_STEPS = ['design', 'size', 'material', 'email'];

  var STEP_TITLES = {
    photo: 'Show us your room',
    style: 'Which style speaks to you?',
    colors: 'Pick your colors',
    shape: 'What shape fits your space?',
    size: 'What size do you need?',
    material: 'Choose your material',
    details: 'Anything else we should know?',
    email: 'Where should we send your designs?',
    design: 'Upload your rug design'
  };

  var STEP_HINTS = {
    photo: 'We’ll read your room’s colors and style — and show every design placed in your actual room.',
    style: 'Tap one to continue',
    colors: 'Pick up to ' + MAX_COLORS + ' — we’ll weave them together',
    shape: '',
    size: 'Priced per square foot — any custom size works',
    material: '',
    details: 'Optional — the more you share, the more personal the design',
    email: 'Your 4 designs are ready to weave. We’ll also save them to your email so you never lose them.',
    design: 'A photo or artwork of the rug you want — we weave it exactly as you send it. JPEG or PNG; larger files weave sharper.'
  };

  /* ---------- wishlist (saved designs, local-first) ---------- */

  var WISHLIST_KEY = 'rqWishlistV1';
  var WISHLIST_MAX = 12;

  function wishlistAll() {
    try {
      var items = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(items) ? items : [];
    } catch (e) { return []; }
  }

  function wishlistSave(items) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(items)); return true; }
    catch (e) { return false; }
  }

  function wishlistAdd(item) {
    var items = wishlistAll().filter(function (it) { return it.id !== item.id; });
    items.unshift(item);
    while (items.length > WISHLIST_MAX) items.pop();
    // localStorage quota — drop oldest until the write fits.
    while (!wishlistSave(items) && items.length) items.pop();
  }

  function wishlistRemove(id) {
    wishlistSave(wishlistAll().filter(function (it) { return it.id !== id; }));
  }

  // Area must mirror the backend's parse_size_to_sqft: round rugs are
  // priced as circles (pi * (d/2)^2, first number = diameter), everything
  // else as width x height.
  // Exact area — what pricing must use. The backend prices off the unrounded
  // footprint, so rounding here first would disagree with the charged price
  // (a 2dp nudge is enough to cross the .99 rounding boundary).
  function sqftExact(size, shape) {
    var parsed = parseCustomSize(size, shape);
    if (!parsed || parsed.roundMismatch) return null;
    return shape === 'round'
      ? Math.PI * Math.pow(parsed.wFt / 2, 2)
      : parsed.wFt * parsed.hFt;
  }

  // Display-rounded area.
  function sqftFor(size, shape) {
    var raw = sqftExact(size, shape);
    return raw == null ? null : Math.round(raw * 100) / 100;
  }

  // Shopper-facing estimate, mirroring the backend's calculate_price so the
  // number shown matches the number charged. Returns null when pricing
  // hasn't loaded — callers show "Price at checkout" instead of guessing.
  function priceFor(material, size, shape) {
    var base = baseRateFor(material);
    var sqft = sqftExact(size, shape);
    if (!base || !sqft) return null;
    // Snap to 6dp before rounding, exactly as the backend does — otherwise
    // float noise on an exact total flips it to the next dollar.
    var raw = Math.round(sqft * base * sizeMultiplier(sqft) * 1e6) / 1e6;
    var price = Math.floor(raw) + 0.99;
    if (price < raw) price = Math.floor(raw) + 1.99;
    return Math.round(price * 100) / 100;
  }

  function wishlistSizeOptions(it) {
    var presets = SIZE_PRESETS[it.shape] || SIZE_PRESETS.rectangular;
    var options = it.size ? [it.size] : [];
    presets.forEach(function (s) { if (options.indexOf(s) === -1) options.push(s); });
    return options;
  }

  function wishlistCardsHTML(items, sel, custom) {
    custom = custom || {};
    return items.map(function (it) {
      var chosen = sel[it.id] || it.size;
      // Current rates always win: a design saved before a price change still
      // carries its old price, and checkout would charge today's.
      // Only today's rates, never the price stored when the design was saved:
      // a rate change would otherwise quote an old number checkout won't honour.
      var price = priceFor(it.material, chosen, it.shape);
      var meta = [it.name, it.style, it.material].filter(Boolean).map(esc).join(' · ');
      var options = wishlistSizeOptions(it);
      var isCustom = options.indexOf(chosen) === -1;
      var cust = custom[it.id] || {};
      return '<div class="rq__result rq__result--wl">' +
        '<span class="rq__result-media"><img src="' + esc(it.src) + '" alt="' + esc(it.style || 'Saved') + ' rug design"' + (it.shape === 'round' ? ' class="rq__img--round"' : '') + '></span>' +
        '<span class="rq__result-label">' + meta + '</span>' +
        '<div class="rq__wl-sizes" role="radiogroup" aria-label="Size">' +
        options.map(function (s) {
          var on = s === chosen;
          return '<button type="button" class="rq__pill rq__pill--sm' + (on ? ' is-selected' : '') + '" role="radio" aria-checked="' + on + '" data-action="wl-size" data-id="' + esc(it.id) + '" data-value="' + esc(s) + '">' + esc(s) + '</button>';
        }).join('') +
        '<button type="button" class="rq__pill rq__pill--sm' + (isCustom || cust.open ? ' is-selected' : '') + '" data-action="wl-size-custom" data-id="' + esc(it.id) + '">' + (isCustom ? esc(chosen) : 'Custom') + '</button>' +
        (cust.open
          ? '<span class="rq__custom-size rq__custom-size--inline">' +
            '<input class="rq__input rq__input--sm" data-input="wl-size-custom-' + esc(it.id) + '" type="text" inputmode="text" placeholder="' + customSizePlaceholder(it.shape) + '" value="' + esc(cust.err ? (cust.val || '') : (isCustom ? chosen : '')) + '" autocomplete="off">' +
            '<button type="button" class="rq__btn rq__btn--primary rq__btn--small" data-action="wl-size-apply" data-id="' + esc(it.id) + '">Apply</button>' +
            (cust.err ? '<span class="rq__microcopy rq__microcopy--error">' + esc(cust.err) + '</span>' : '') +
            '</span>'
          : '') +
        '</div>' +
        '<div class="rq__wl-foot">' +
        (price != null ? '<span class="rq__price">' + fmtMoney(price, it.currency) + (chosen !== it.size ? ' <small>est.</small>' : '') + '</span>' : '<span class="rq__price rq__price--tbd">Price at checkout</span>') +
        '<span class="rq__wl-actions">' +
        '<button type="button" class="rq__btn rq__btn--primary rq__btn--small" data-action="wl-add" data-id="' + esc(it.id) + '">Add to cart</button>' +
        '<button type="button" class="rq__btn rq__btn--ghost rq__btn--small" data-action="wishlist-remove" data-id="' + esc(it.id) + '">Remove</button>' +
        '</span></div>' +
        '</div>';
    }).join('');
  }

  // Shared wishlist card behavior for the quiz app AND the standalone saved
  // page. host: {root, config, _wlSel, render()}. Returns true if handled.
  function wishlistLoginNote(config) {
    if (!config || !config.proxyPath || config.mock) return '';
    if (config.customerId) {
      return '<p class="rq__microcopy">✓ Synced to your account.</p>';
    }
    return '<p class="rq__microcopy">Saved on this device. <a class="rq__link" href="/account/login?return_url=/pages/saved-designs">Log in</a> to keep your designs on every device.</p>';
  }

  // Logged-in shoppers: pull account favorites from the backend (via the
  // signed proxy), merge into the local list, and adopt local anonymous
  // designs into the account. Runs once per page view, best-effort.
  function wishlistSyncAccount(config, done) {
    if (!config || !config.proxyPath || config.mock || !config.customerId) return;
    var api = new HttpRugApi('', config.proxyPath);
    var locals = wishlistAll();
    var genIds = [];
    locals.forEach(function (it) { if (it.generationId && genIds.indexOf(it.generationId) === -1) genIds.push(it.generationId); });
    if (genIds.length) {
      api._fetch('/wishlist/claim/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ generation_ids: genIds })
      }).catch(function () {});
    }
    api._fetch('/favorites/', { method: 'GET' }).then(function (body) {
      var known = {};
      wishlistAll().forEach(function (it) { known[it.id] = true; });
      var added = 0;
      (body.results || []).forEach(function (fav) {
        var id = fav.generation_id + '-' + fav.index;
        if (known[id]) return;
        var gen = fav.generation || {};
        wishlistAdd({
          id: id,
          generationId: fav.generation_id,
          index: fav.index,
          src: api.urlFor('/' + fav.generation_id + '/image/' + fav.index + '/'),
          name: fav.name || '',
          style: gen.style, size: gen.size, material: gen.material, shape: gen.shape,
          price: priceFor(gen.material, gen.size, gen.shape),
          currency: 'USD', savedAt: Date.now()
        });
        added++;
      });
      if (added && done) done();
    }).catch(function () {});
  }

  function handleWishlistAction(host, action, el) {
    if (!host._wlCustom) host._wlCustom = {};
    if (action === 'wl-size') {
      var pickId = el.getAttribute('data-id');
      host._wlSel[pickId] = el.getAttribute('data-value');
      host._wlCustom[pickId] = null;
      host.render();
      return true;
    }
    if (action === 'wl-size-custom') {
      var cId = el.getAttribute('data-id');
      var cur = host._wlCustom[cId];
      host._wlCustom[cId] = cur && cur.open ? null : { open: true };
      host.render();
      return true;
    }
    if (action === 'wl-size-apply') {
      var aId = el.getAttribute('data-id');
      var aItem = null;
      wishlistAll().forEach(function (it) { if (it.id === aId) aItem = it; });
      var aShape = aItem ? aItem.shape : 'rectangular';
      // Attribute-match lookup (ids can hold selector metacharacters).
      var input = Array.prototype.filter.call(
        host.root.querySelectorAll('input[data-input]'),
        function (n) { return n.getAttribute('data-input') === 'wl-size-custom-' + aId; }
      )[0];
      var raw = input ? input.value.trim() : '';
      if (!customSizeOk(raw, aShape)) {
        host._wlCustom[aId] = { open: true, err: customSizeError(raw, aShape), val: raw };
      } else {
        host._wlSel[aId] = raw;
        host._wlCustom[aId] = null;
      }
      host.render();
      return true;
    }
    if (action === 'wl-add') {
      var id = el.getAttribute('data-id');
      var item = null;
      wishlistAll().forEach(function (it) { if (it.id === id) item = it; });
      if (!item) return true;
      var size = host._wlSel[id] || item.size;
      track('wishlist_add_to_cart', { size: size, material: item.material });
      var genId = item.generationId;
      var index = item.index;
      if (genId == null || index == null) {
        // Legacy item — id is "<generationId>-<index>".
        var m = /^(.*)-(\d+)$/.exec(id);
        if (m) { genId = m[1]; index = parseInt(m[2], 10); }
      }
      var wlHasBackend = host.config.apiBaseUrl || host.config.proxyPath;
      if (host.config.mock || !wlHasBackend || genId == null) {
        var price = priceFor(item.material, size, item.shape);
        var dialog = host.root.querySelector('[data-demo-dialog]');
        if (dialog) {
          dialog.querySelector('[data-demo-body]').innerHTML =
            '<p><strong>Demo mode</strong> — in the live store this adds the saved design to your cart in the chosen size.</p>' +
            '<p>' + [item.style, size, item.material].filter(Boolean).map(esc).join(' · ') +
            (price != null ? ' · ' + fmtMoney(price, item.currency) : '') + '</p>';
          dialog.showModal();
        }
        return true;
      }
      el.disabled = true;
      el.textContent = 'Preparing…';
      new HttpRugApi(host.config.apiBaseUrl, host.config.proxyPath)
        .checkout(genId, index, size !== item.size ? size : null)
        .then(function (res) {
          if (res.variantId && themeCartAvailable(host.config)) {
            return addVariantToThemeCart(res.variantId, 1).then(function () {
              track('added_to_theme_cart', { variant_id: res.variantId, source: 'wishlist' });
              el.disabled = false; el.textContent = 'Added ✓';
              setTimeout(function () { el.textContent = 'Add to cart'; }, 2500);
            }).catch(function () {
              window.location.href = res.checkoutUrl;
            });
          }
          if (res.checkoutUrl) { window.location.href = res.checkoutUrl; return; }
          el.disabled = false; el.textContent = 'Add to cart';
        })
        .catch(function (err) {
          el.disabled = false; el.textContent = 'Add to cart';
          alert((err && err.message) || 'Checkout failed — please try again.');
        });
      return true;
    }
    return false;
  }

  function App(root, config) {
    this.root = root;
    this.config = config || {};
    // Merchant-defined styles from the section's blocks (Shopify theme
    // editor); falls back to the built-in ten when no blocks are set.
    var cfgStyles = (this.config.styles || []).filter(function (s) { return s && s.id; });
    this.styles = cfgStyles.length ? cfgStyles : STYLES;
    this.api = this.config.mock ? new MockRugApi() : new HttpRugApi(this.config.apiBaseUrl, this.config.proxyPath);
    // Rates always come from the backend, even in demo mode — demo fakes the
    // designs, never the money.
    this.pricingApi = new HttpRugApi(this.config.apiBaseUrl, this.config.proxyPath);
    this.phase = 'quiz'; // quiz | generating | results
    this.stepIndex = 0;
    this.uploadMode = false; // true = "order your own design" path
    this.designPx = null;    // {w, h} of the processed design upload
    this.answers = {
      roomPhoto: null, // downscaled JPEG data URL, kept in memory only
      designImage: null, // customer-uploaded artwork data URL (memory only)
      style: null,
      colors: [],
      shape: 'rectangular',
      size: null,
      customSize: '',
      material: null,
      description: '',
      email: ''
    };
    this.roomPalette = null;    // [{name, hex}] extracted from the photo
    this.suggestedStyle = null; // style suggested from the photo
    this.prefilledFromPhoto = false;
    this.photoError = null;
    this.photoBusy = false;
    this.generation = null; // {generationId, pricing, images, remaining}
    this.selectedIndex = null;
    this.composites = {};   // index -> {status: 'loading'|'done'|'error', src}
    this.cardView = {};     // index -> 'rug' | 'room'
    this.error = null;
    this._pollTimer = null;
    this._lineTimer = null;
    this._restore();
    identify(this.config);
    // Logged-in shoppers skip typing their email — designs auto-link to
    // their account via the signed App Proxy.
    if (!this.answers.email && this.config.customerEmail) {
      this.answers.email = this.config.customerEmail;
    }
    this.render();
    this._loadPricing();
    track('start', { mock: !!this.config.mock });
  }

  // Fetch the published rate table + size curve, then repaint any prices
  // that were withheld while it was in flight. A failure is not fatal: the
  // UI simply says "Price at checkout" instead of showing a wrong number.
  App.prototype._loadPricing = function () {
    var self = this;
    if (PRICING) return;
    if (!this.config.apiBaseUrl && !this.config.proxyPath) return;
    this.pricingApi.options().then(function (body) {
      if (applyPricingConfig(body && body.pricing)) self.render();
    }).catch(function () { /* prices stay hidden until checkout */ });
  };

  App.prototype._storageKey = 'rugQuizAnswersV1';

  // Active step list — the upload path is shorter than the AI quiz.
  App.prototype._steps = function () {
    return this.uploadMode ? UPLOAD_STEPS : STEP_ORDER;
  };

  App.prototype._persist = function () {
    // roomPhoto/designImage are large data URLs — memory only, never stored.
    var slim = Object.assign({}, this.answers, {
      roomPhoto: undefined,
      designImage: undefined,
      _step: this.stepIndex,
      _mode: this.uploadMode ? 'upload' : 'quiz'
    });
    try { sessionStorage.setItem(this._storageKey, JSON.stringify(slim)); } catch (e) { /* private mode */ }
  };

  App.prototype._restore = function () {
    try {
      var saved = JSON.parse(sessionStorage.getItem(this._storageKey) || 'null');
      if (saved && typeof saved === 'object') {
        // Mode first — the step clamp below depends on which flow is active.
        this.uploadMode = saved._mode === 'upload';
        delete saved._mode;
        // Return the shopper to where they left off (e.g. after the login
        // round-trip) instead of step 1 with prefilled answers.
        if (typeof saved._step === 'number' && isFinite(saved._step)) {
          this.stepIndex = Math.max(0, Math.min(this._steps().length - 1, Math.floor(saved._step)));
        }
        delete saved._step;
        Object.assign(this.answers, saved);
      }
    } catch (e) { /* ignore corrupt state */ }
    this._restoreGeneration();
    try {
      var url = new URL(window.location.href);
      var dirty = false;
      var styleParam = url.searchParams.get('style');
      if (styleParam) {
        var wanted = styleParam.toLowerCase();
        var match = this.styles.filter(function (s) { return String(s.id).toLowerCase() === wanted; })[0];
        if (match) {
          this.uploadMode = false;
          if (this.answers.style && this.answers.style !== match.id) {
            // Swapping a mid-quiz style must be visible — land on the style
            // step instead of silently changing it deep in the quiz.
            this.stepIndex = Math.max(0, STEP_ORDER.indexOf('style'));
          }
          this.answers.style = match.id;
          // A style tile click means "design a new rug" — show the quiz even
          // when a previous generation was restored.
          this.phase = 'quiz';
        }
        url.searchParams.delete('style');
        dirty = true;
      }
      if (url.searchParams.get('mode') === 'upload') {
        if (!this.uploadMode) {
          this.uploadMode = true;
          this.stepIndex = 0;
        }
        this.phase = 'quiz';
        url.searchParams.delete('mode');
        dirty = true;
      }
      if (dirty) {
        // One-shot: strip the params so a later reload restores the shopper's
        // own saved choices instead of re-applying the deep link.
        window.history.replaceState(null, '', url.pathname + url.search + url.hash);
      }
    } catch (e) { /* no deep-link */ }
  };

  App.prototype._genKey = 'rugQuizGenV1';

  // The four woven designs must survive the shopper wandering off to another
  // page: persist a slim generation snapshot and reopen the results view on
  // return, instead of making them burn another free session to see it again.
  App.prototype._persistGeneration = function () {
    var gen = this.generation;
    if (!gen || !gen.images || !gen.images.length) return;
    var self = this;
    var slim = {
      generationId: gen.generationId,
      pricing: gen.pricing,
      remaining: gen.remaining,
      mock: !!this.config.mock,
      source: gen.source || 'generated',
      selectedIndex: this.selectedIndex,
      images: gen.images.map(function (img, i) {
        var src = img.src;
        // Live legacy responses inline multi-MB base64 — swap in the stable
        // image endpoint instead of blowing the sessionStorage quota. Demo
        // SVG data URIs are tiny and kept as-is.
        if (!self.config.mock && /^data:/.test(src) && typeof self.api.urlFor === 'function') {
          src = self.api.urlFor('/' + gen.generationId + '/image/' + i + '/');
        }
        return { src: src, isFavorite: !!img.isFavorite, name: img.name || '' };
      })
    };
    try { sessionStorage.setItem(this._genKey, JSON.stringify(slim)); } catch (e) { /* private mode/quota */ }
  };

  App.prototype._restoreGeneration = function () {
    try {
      var saved = JSON.parse(sessionStorage.getItem(this._genKey) || 'null');
      if (!saved || !saved.images || !saved.images.length) return;
      // Never mix modes: a demo generation must not be orderable in live.
      if (!!saved.mock !== !!this.config.mock) return;
      this.generation = {
        generationId: saved.generationId,
        pricing: saved.pricing,
        remaining: saved.remaining,
        source: saved.source || 'generated',
        images: saved.images.map(function (img, i) {
          return { index: i, src: img.src, isFavorite: !!img.isFavorite, name: img.name || '' };
        })
      };
      if (this.generation.source === 'upload') this.uploadMode = true;
      this.selectedIndex = typeof saved.selectedIndex === 'number' ? saved.selectedIndex : null;
      this.phase = 'results';
    } catch (e) { /* corrupt snapshot — start fresh */ }
  };

  App.prototype.goTo = function (index) {
    this.stepIndex = Math.max(0, Math.min(this._steps().length - 1, index));
    this.phase = 'quiz';
    this.error = null;
    this._persist();
    this.render();
    track('step_view', { step: this._steps()[this.stepIndex], position: this.stepIndex + 1 });
  };

  App.prototype.next = function () {
    this._persist();
    track('step_complete', { step: this._steps()[this.stepIndex] });
    if (this.stepIndex < this._steps().length - 1) {
      this.goTo(this.stepIndex + 1);
    } else {
      this.startGeneration();
    }
  };

  App.prototype.sizeValue = function () {
    return this.answers.size === '__custom__' ? this.answers.customSize.trim() : this.answers.size;
  };

  App.prototype.handleDesignFile = function (file) {
    var self = this;
    if (!file) return;
    if (!/^image\//.test(file.type || '')) {
      this.photoError = 'Please upload an image file (JPEG or PNG).';
      this.render();
      return;
    }
    this.photoBusy = true;
    this.photoError = null;
    this.render();
    processDesignFile(file).then(function (res) {
      self.photoBusy = false;
      if (Math.min(res.px.w, res.px.h) < 600) {
        self.photoError = 'That image is too small to weave sharply — we need at least 600px on the short side (yours is ' + res.px.w + '×' + res.px.h + ').';
        self.render();
        return;
      }
      self.answers.designImage = res.dataUrl;
      self.designPx = res.px;
      self._persist();
      self.render();
      track('upload_design_added', { w: res.px.w, h: res.px.h });
    }).catch(function (err) {
      self.photoBusy = false;
      self.photoError = err && err.message ? err.message : 'We couldn’t read that image — please try another.';
      self.render();
    });
  };

  // Upload flow has no shape step — the rug keeps the artwork's own shape and
  // is sized/priced by its width x length bounding box. Quiz state in
  // answers.shape stays untouched so switching flows never corrupts it.
  App.prototype.effShape = function () {
    return this.uploadMode ? 'rectangular' : this.answers.shape;
  };

  // Print-quality estimate for the chosen size: pixels per woven foot.
  App.prototype.designQuality = function () {
    if (!this.designPx || !this.uploadMode) return null;
    var parsed = parseCustomSize(this.sizeValue(), this.effShape());
    if (!parsed || parsed.roundMismatch) return null;
    var ftLong = Math.max(parsed.wFt, parsed.hFt);
    var ftShort = Math.min(parsed.wFt, parsed.hFt);
    var pxLong = Math.max(this.designPx.w, this.designPx.h);
    var pxShort = Math.min(this.designPx.w, this.designPx.h);
    var ppf = Math.min(pxLong / ftLong, pxShort / ftShort);
    if (ppf >= 150) return { level: 'great', text: '✓ Excellent print quality at this size' };
    if (ppf >= 80) return { level: 'ok', text: '✓ Good print quality at this size' };
    return { level: 'low', text: 'Heads up: your image may weave soft at this size — a smaller rug or a larger file will look sharper.' };
  };

  App.prototype.handlePhotoFile = function (file) {
    var self = this;
    if (!file) return;
    this.photoBusy = true;
    this.photoError = null;
    this.render();
    analyzeRoomPhoto(file).then(function (res) {
      self.photoBusy = false;
      self.answers.roomPhoto = res.dataUrl;
      self.roomPalette = res.palette;
      self.suggestedStyle = res.style;
      // Prefill only what the user hasn't already chosen.
      if (!self.answers.style && res.style) self.answers.style = res.style;
      if (!self.answers.colors.length && res.palette.length) {
        self.answers.colors = res.palette.map(function (p) { return p.name; });
        self.prefilledFromPhoto = true;
      }
      self._persist();
      self.render();
      track('photo_uploaded', { suggested_style: res.style });
    }).catch(function (err) {
      self.photoBusy = false;
      self.photoError = err && err.message ? err.message : 'We couldn’t read that image — please try another.';
      self.render();
    });
  };

  App.prototype.placeDesignInRoom = function (index) {
    var self = this;
    if (!this.answers.roomPhoto || !this.generation) return;
    var existing = this.composites[index];
    if (existing && existing.status === 'loading') return;
    if (existing && existing.status === 'done') {
      this.cardView[index] = 'room';
      this.render();
      return;
    }
    this.composites[index] = { status: 'loading' };
    this.render();
    track('room_place_requested', { index: index });
    // A slow /place/ response can outlive a "Weave 4 more" regeneration —
    // only apply it if the generation it belongs to is still on screen.
    var genId = this.generation.generationId;
    this.api.placeInRoom(genId, index, this.answers.roomPhoto).then(function (res) {
      if (!self.generation || self.generation.generationId !== genId) return;
      self.composites[index] = { status: 'done', src: res.src };
      self.cardView[index] = 'room';
      if (self.phase === 'results') self.render();
      track('room_place_complete', { index: index });
    }).catch(function (err) {
      if (!self.generation || self.generation.generationId !== genId) return;
      self.composites[index] = { status: 'error', message: err && err.message };
      if (self.phase === 'results') self.render();
    });
  };

  App.prototype.startGeneration = function () {
    var self = this;
    if (this.uploadMode) return this.startUpload();
    this.phase = 'generating';
    this.error = null;
    this.orderSize = null;
    this.sizeEditOpen = false;
    this.sizeCustomOpen = false;
    this._sizeCustomErr = null;
    this._sizeCustomVal = null;
    this.render();
    track('generation_requested', { style: this.answers.style, material: this.answers.material });

    var payload = {
      email: this.answers.email.trim(),
      style: this.answers.style,
      size: this.sizeValue(),
      material: this.answers.material,
      shape: this.answers.shape,
      colors: this.answers.colors,
      description: this.answers.description.trim(),
      // Room photo rides along so the backend can bias the design palette
      // and reuse it for /place/ without a second upload.
      room_image_base64: this.answers.roomPhoto || undefined
    };

    this.api.generate(payload).then(function (res) {
      self.generation = { generationId: res.generationId, pricing: res.pricing, images: [], remaining: res.generationsRemaining };
      self._pollUntilReady(Date.now());
    }).catch(function (err) {
      self._fail(err);
    });
  };

  // "Order your own design": no Gemini, no quota — one upload call, then
  // straight to the review screen.
  App.prototype.startUpload = function () {
    var self = this;
    var rights = this.root.querySelector('[data-input="rights"]');
    if (!rights || !rights.checked) {
      this.error = 'Please confirm you have the rights to use this design.';
      this.render();
      return;
    }
    if (!this.answers.designImage) {
      this.error = 'Please add your design image first.';
      this.stepIndex = 0;
      this.render();
      return;
    }
    this.phase = 'generating';
    this.error = null;
    this.orderSize = null;
    this.sizeEditOpen = false;
    this.sizeCustomOpen = false;
    this._sizeCustomErr = null;
    this._sizeCustomVal = null;
    this.render();
    track('upload_submitted', { material: this.answers.material, size: this.sizeValue() });

    this.api.uploadDesign({
      design_image_base64: this.answers.designImage,
      email: this.answers.email.trim(),
      size: this.sizeValue(),
      material: this.answers.material,
      shape: this.effShape(),
      description: this.answers.description.trim(),
      rights_confirmed: true
    }).then(function (res) {
      self.generation = {
        generationId: res.generationId,
        pricing: res.pricing,
        images: [{ index: 0, src: res.src, isFavorite: false, name: res.name || '' }],
        remaining: null,
        source: 'upload'
      };
      self.selectedIndex = 0;
      self.phase = 'results';
      self.composites = {};
      self.cardView = {};
      self.render();
      self._persistGeneration();
      track('upload_ready', {});
    }).catch(function (err) {
      self._fail(err);
    });
  };

  App.prototype._pollUntilReady = function (startedAt) {
    var self = this;
    if (this.phase !== 'generating') return;
    if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
      return this._fail(new Error('This is taking longer than usual. Your designs may still be weaving — please try again in a minute.'));
    }
    this.api.poll(this.generation.generationId).then(function (res) {
      if (self.phase !== 'generating') return;
      if (res.status === 'generated' && res.images.length) {
        self.generation.images = res.images;
        if (res.pricing) self.generation.pricing = res.pricing;
        self.phase = 'results';
        self.selectedIndex = null;
        self.composites = {};
        self.cardView = {};
        self.render();
        self._persistGeneration();
        track('generation_complete', { count: res.images.length });
        // The in-room view is the magic moment — lead with design 1 placed.
        if (self.answers.roomPhoto) self.placeDesignInRoom(0);
      } else if (res.status === 'failed') {
        self._fail(new Error('We hit a snag weaving your designs. Please try again.'));
      } else {
        self._pollTimer = setTimeout(function () { self._pollUntilReady(startedAt); }, POLL_INTERVAL_MS);
      }
    }).catch(function (err) {
      self._fail(err);
    });
  };

  App.prototype._fail = function (err) {
    this.phase = 'quiz';
    this.stepIndex = this._steps().length - 1; // back to the email/generate step
    this.error = err && err.message ? err.message : 'Something went wrong. Please try again.';
    // A failed re-weave shouldn't strand the shopper: bring the previous
    // batch back into memory so "Back to my 4 designs" still works.
    if (!this.generation || !this.generation.images || !this.generation.images.length) {
      var failedPhase = this.phase;
      this._restoreGeneration();
      this.phase = failedPhase;
    }
    this.render();
    track('generation_error', { message: this.error });
  };

  /* ---------- theme cart integration ----------
   * On the storefront the finished rug goes into the Shopify cart via the
   * theme's own ajax flow, so the cart drawer opens in place — with the
   * merchant's upsells under the item — instead of jumping to checkout.
   * Mirrors Dwell's product-form publish contract (@shopify/events).
   */
  function addVariantToThemeCart(variantId, qty) {
    return import('@shopify/events').then(function (events) {
      var CartLinesUpdateEvent = events.CartLinesUpdateEvent;
      var quantity = qty || 1;
      var formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', String(quantity));
      var sectionIds = [];
      document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
        var sid = el.getAttribute('data-section-id');
        if (sid && sectionIds.indexOf(sid) === -1) sectionIds.push(sid);
      });
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      var deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: quantity }],
        promise: deferred.promise
      }));

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      }).then(function (r) { return r.json(); }).then(function (response) {
        if (response.status) throw new Error(response.message || 'Add to cart failed');
        return fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { return r.json(); })
          .then(function (ajaxCart) {
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
              detail: {
                items: ajaxCart.items,
                source: 'rug-quiz',
                sourceId: 'rug-quiz',
                itemCount: quantity,
                sections: response.sections,
                didError: false
              }
            });
            return ajaxCart;
          });
      }).catch(function (err) {
        try { deferred.reject(err); } catch (e) { /* already settled */ }
        throw err;
      });
    });
  }

  // Storefront pages have the drawer + importmap; the dev harness does not.
  function themeCartAvailable(config) {
    return !!(config && config.proxyPath) && !!document.querySelector('cart-items-component[data-section-id]');
  }

  App.prototype.doCheckout = function () {
    var self = this;
    if (this.selectedIndex == null || !this.generation) return;
    var btn = this.root.querySelector('[data-action="checkout"]');
    if (btn) { btn.disabled = true; btn.textContent = 'Preparing checkout…'; }
    track('checkout_started', { index: this.selectedIndex, size: this.orderSize || this.sizeValue() });
    this.api.checkout(this.generation.generationId, this.selectedIndex, this.orderSize || null).then(function (res) {
      if (res.variantId && themeCartAvailable(self.config)) {
        // Stay on the page: the drawer opens with the rug and the upsells.
        return addVariantToThemeCart(res.variantId, 1).then(function () {
          track('added_to_theme_cart', { variant_id: res.variantId });
          if (btn) { btn.disabled = false; btn.textContent = 'Added ✓'; }
          setTimeout(function () {
            var b = self.root.querySelector('[data-action="checkout"]');
            if (b) b.textContent = 'Add to cart';
          }, 2500);
        }).catch(function () {
          // Ajax cart failed — the permalink still completes the purchase.
          track('checkout_redirect', { fallback: true });
          window.location.href = res.checkoutUrl;
        });
      }
      if (res.checkoutUrl) {
        track('checkout_redirect', {});
        window.location.href = res.checkoutUrl;
      } else {
        self._showDemoCheckout(res);
        if (btn) { btn.disabled = false; btn.textContent = 'Add to cart'; }
      }
    }).catch(function (err) {
      if (btn) { btn.disabled = false; btn.textContent = 'Add to cart'; }
      alert(err.message || 'Checkout failed — please try again.');
    });
  };

  App.prototype._showDemoCheckout = function (res) {
    var dialog = this.root.querySelector('[data-demo-dialog]');
    if (!dialog) return;
    var pricing = this.generation.pricing || {};
    var shownPrice = res && res.price != null ? res.price : this.effectivePrice();
    var price = shownPrice != null ? fmtMoney(shownPrice, pricing.currency) : '';
    dialog.querySelector('[data-demo-body]').innerHTML =
      '<p><strong>Demo mode</strong> — in the live store this button sends the chosen design to Shopify and redirects to checkout.</p>' +
      '<p>' + esc(((this.generation && this.generation.images[this.selectedIndex]) || {}).name || (this.generation && this.generation.source === 'upload' ? 'Your design' : 'Design ' + (this.selectedIndex + 1))) + ' · ' +
      (this.generation && this.generation.source === 'upload' ? '' : esc(this.answers.style) + ' · ') + esc(this.orderSize || this.sizeValue()) + '<br>' +
      esc(this.answers.material) + (price ? ' · ' + price : '') + '</p>';
    dialog.showModal();
  };

  /* ---------- rendering ---------- */

  App.prototype.render = function () {
    if (this._pollTimer && this.phase !== 'generating') { clearTimeout(this._pollTimer); this._pollTimer = null; }
    if (this._lineTimer) { clearInterval(this._lineTimer); this._lineTimer = null; }

    var html;
    if (this.phase === 'generating') html = this.renderGenerating();
    else if (this.phase === 'results') html = this.renderResults();
    else if (this.phase === 'wishlist') html = this.renderWishlist();
    else html = this.renderStep();

    // Demo mode is always labeled honestly; the live-backend badge is an
    // internal testing aid and only shows under an explicit ?api= override.
    var apiOverride = null;
    try { apiOverride = sessionStorage.getItem('rq-api-mode'); } catch (e) { /* private mode */ }
    var badge = '';
    if (this.config.mock) {
      badge = '<div class="rq__demo-badge">Demo mode — sample designs, no live AI yet</div>';
    } else if (apiOverride === 'live') {
      badge = '<div class="rq__demo-badge rq__demo-badge--live">Live AI — designs from ' + esc((this.config.apiBaseUrl || '').replace(/^https?:\/\//, '')) + '</div>';
    }
    this.root.innerHTML =
      '<div class="rq">' +
      badge +
      html +
      '<dialog class="rq__dialog" data-demo-dialog><div data-demo-body></div>' +
      '<button class="rq__btn rq__btn--primary" data-action="close-dialog" type="button">Close</button></dialog>' +
      '</div>';

    this.bind();

    // Move focus to the heading only when the view changes (step/phase
    // navigation) — not on same-view re-renders like toggling a color or
    // selecting a design, where a focus jump would scroll the page away.
    var viewKey = this.phase + ':' + (this.phase === 'quiz' ? this.stepIndex : '');
    var heading = this.root.querySelector('.rq__title');
    if (heading) heading.setAttribute('tabindex', '-1');
    if (heading && this._lastViewKey && this._lastViewKey !== viewKey) {
      heading.focus({ preventScroll: false });
    }
    this._lastViewKey = viewKey;
  };

  App.prototype.renderProgress = function () {
    var pct = ((this.stepIndex + 1) / this._steps().length) * 100;
    return '<div class="rq__progress" role="progressbar" aria-valuemin="1" aria-valuemax="' + this._steps().length + '" aria-valuenow="' + (this.stepIndex + 1) + '" aria-label="Quiz progress">' +
      '<div class="rq__progress-meta">' +
      // Back lives in the sticky action bar now — the header keeps only the
      // counter so the two never disagree about where "go back" is.
      '<span></span>' +
      '<span class="rq__progress-count">' + (this.stepIndex + 1) + ' / ' + this._steps().length + '</span>' +
      '</div>' +
      '<div class="rq__progress-track"><div class="rq__progress-fill" style="width:' + pct + '%"></div></div>' +
      '</div>';
  };

  App.prototype.renderStep = function () {
    var step = this._steps()[this.stepIndex];
    var body = '';
    if (step === 'photo') body = this.renderPhotoStep();
    else if (step === 'design') body = this.renderDesignStep();
    else if (step === 'style') body = this.renderStyleStep();
    else if (step === 'colors') body = this.renderColorsStep();
    else if (step === 'shape') body = this.renderShapeStep();
    else if (step === 'size') body = this.renderSizeStep();
    else if (step === 'material') body = this.renderMaterialStep();
    else if (step === 'details') body = this.renderDetailsStep();
    else if (step === 'email') body = this.renderEmailStep();

    // Steps that advance on selection (style, material) render no Continue,
    // so they would otherwise carry no actions bar and lose Back entirely now
    // that it has moved out of the header. Give them a Back-only bar.
    if (body.indexOf('rq__actions--bar') === -1 && this.stepIndex > 0) {
      body += this.stepActions('');
    }

    var saved = wishlistAll().length;
    var hasResults = this.generation && this.generation.images && this.generation.images.length;
    return this.renderProgress() +
      (hasResults
        ? '<p class="rq__results-return"><button type="button" class="rq__link" data-action="show-results">← Back to my ' + (this.generation.source === 'upload' ? 'design' : '4 designs') + '</button></p>'
        : '') +
      '<h2 class="rq__title">' + this.stepTitle(step) + '</h2>' +
      (this.stepHint(step) ? '<p class="rq__hint">' + this.stepHint(step) + '</p>' : '') +
      (this.error ? '<p class="rq__error" role="alert">' + esc(this.error) + '</p>' : '') +
      body +
      (saved > 0
        ? '<p class="rq__wishlist-link"><button type="button" class="rq__link" data-action="open-wishlist">♥ Saved designs (' + saved + ')</button></p>'
        : '');
  };

  // Step heading/hint — the style step's copy is merchant-editable via the
  // section settings; everything else uses the built-in strings.
  App.prototype.stepTitle = function (step) {
    if (step === 'style' && this.config.styleHeading) return esc(this.config.styleHeading);
    if (step === 'email' && this.uploadMode) return 'Almost there';
    return STEP_TITLES[step];
  };

  App.prototype.stepHint = function (step) {
    if (step === 'style' && this.config.styleSubheading != null && this.config.styleSubheading !== '') {
      return esc(this.config.styleSubheading);
    }
    if (step === 'email' && this.uploadMode) {
      return 'Confirm the details and we’ll show your rug, priced and ready to order.';
    }
    return STEP_HINTS[step];
  };

  App.prototype.renderWishlist = function () {
    var items = wishlistAll();
    if (!this._wlSel) this._wlSel = {};
    return '<div class="rq__wishlist">' +
      '<h2 class="rq__title">Saved designs</h2>' +
      (items.length
        ? '<p class="rq__hint">Designs you hearted — saved on this device. Pick a size and order any of them.</p>' +
          '<div class="rq__grid rq__grid--results">' + wishlistCardsHTML(items, this._wlSel, this._wlCustom) + '</div>'
        : '<p class="rq__hint">Nothing saved yet — tap the ♥ on any design you like and it will wait for you here.</p>') +
      wishlistLoginNote(this.config) +
      '<div class="rq__actions"><button class="rq__btn rq__btn--primary" data-action="close-wishlist" type="button">' +
      (this._wishlistReturn === 'results' ? 'Back to my designs' : 'Back') + '</button></div>' +
      '</div>';
  };

  App.prototype.renderPhotoStep = function () {
    if (this.photoBusy) {
      return '<div class="rq__dropzone rq__dropzone--busy"><div class="rq__loom" aria-hidden="true"><span></span><span></span><span></span><span></span></div>' +
        '<p class="rq__hint">Reading your room’s colors and light…</p></div>';
    }
    if (this.answers.roomPhoto) {
      var palette = this.roomPalette || [];
      return '<div class="rq__photo-preview"><img src="' + this.answers.roomPhoto + '" alt="Your room"></div>' +
        (palette.length
          ? '<div class="rq__palette-row"><span class="rq__palette-label">Your room’s palette</span>' +
            palette.map(function (p) {
              return '<span class="rq__chip"><span class="rq__dot" style="background:' + p.hex + '"></span>' + esc(p.name) + '</span>';
            }).join('') + '</div>'
          : '') +
        (this.suggestedStyle
          ? '<p class="rq__microcopy">We’ll suggest <strong>' + esc(this.suggestedStyle) + '</strong> style and these colors — you can change anything in the next steps.</p>'
          : '') +
        this.stepActions(
          '<button class="rq__btn rq__btn--ghost" data-action="remove-photo" type="button">Use a different photo</button>' +
          '<button class="rq__btn rq__btn--primary" data-action="continue" type="button">Looks good — continue</button>'
        );
    }
    return (this.photoError ? '<p class="rq__error" role="alert">' + esc(this.photoError) + '</p>' : '') +
      '<div class="rq__dropzone" data-dropzone role="button" tabindex="0" aria-label="Add a photo of your room">' +
      '<svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true"><path d="M4 7h3l2-2h6l2 2h3v13H4z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="12" cy="13" r="3.4" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>' +
      '<p class="rq__dropzone-title">Add a photo of your room</p>' +
      '<p class="rq__microcopy">Snap it from the doorway with the floor in view — daylight works best.</p>' +
      '<span class="rq__btn rq__btn--primary">Upload or take a photo</span>' +
      '<input type="file" accept="image/*" data-input="photo" hidden>' +
      '</div>' +
      '<div class="rq__actions rq__actions--center">' +
      '<button class="rq__btn rq__btn--ghost" data-action="skip-photo" type="button">Continue without a photo</button>' +
      '</div>' +
      '<p class="rq__microcopy rq__upload-entry">Already have a design? <button type="button" class="rq__link" data-action="enter-upload">Upload it and order it as-is →</button></p>';
  };

  // "Order your own design": the artwork dropzone. Unlike the room photo we
  // keep resolution — it IS the print quality (capped at 3000px for payload).
  App.prototype.renderDesignStep = function () {
    if (this.photoBusy) {
      return '<div class="rq__dropzone rq__dropzone--busy"><div class="rq__loom" aria-hidden="true"><span></span><span></span><span></span><span></span></div>' +
        '<p class="rq__hint">Reading your design…</p></div>';
    }
    if (this.answers.designImage) {
      var px = this.designPx;
      return '<div class="rq__photo-preview rq__design-preview"><img src="' + this.answers.designImage + '" alt="Your rug design"></div>' +
        (px ? '<p class="rq__microcopy">' + px.w + '×' + px.h + 'px — you’ll see the print-quality check once you pick a size.</p>' : '') +
        '<div class="rq__actions">' +
        '<button class="rq__btn rq__btn--ghost" data-action="remove-design" type="button">Use a different image</button>' +
        '<button class="rq__btn rq__btn--primary" data-action="continue" type="button">Continue</button>' +
        '</div>';
    }
    return (this.photoError ? '<p class="rq__error" role="alert">' + esc(this.photoError) + '</p>' : '') +
      '<div class="rq__dropzone" data-design-dropzone role="button" tabindex="0" aria-label="Upload your rug design">' +
      '<svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true"><path d="M4 5h16v14H4z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M4 15l4.5-4.5 4 4L16 11l4 4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="9" cy="9" r="1.4" fill="currentColor"/></svg>' +
      '<p class="rq__dropzone-title">Upload your design</p>' +
      '<p class="rq__microcopy">At least 600px on the short side. We’ll send a final proof for your approval before weaving.</p>' +
      '<span class="rq__btn rq__btn--primary">Choose an image</span>' +
      '<input type="file" accept="image/*" data-input="design-file" hidden>' +
      '</div>' +
      '<p class="rq__microcopy rq__upload-entry">Changed your mind? <button type="button" class="rq__link" data-action="exit-upload">Design one with AI instead →</button></p>';
  };

  App.prototype.renderStyleStep = function () {
    var self = this;
    return '<div class="rq__grid rq__grid--styles" role="radiogroup" aria-label="Rug style">' +
      this.styles.map(function (s) {
        var selected = self.answers.style === s.id;
        var suggested = self.suggestedStyle === s.id && self.answers.roomPhoto;
        // Merchant-uploaded tile image wins; generated art is the fallback.
        var art = s.image
          ? '<img src="' + esc(s.image) + '" alt="" loading="lazy">'
          : '<img src="' + rugSVG({ style: s.id, seed: 4 }) + '" alt="" loading="lazy">';
        return '<button type="button" class="rq__tile' + (selected ? ' is-selected' : '') + '" role="radio" aria-checked="' + selected + '" data-action="pick-style" data-value="' + esc(s.id) + '">' +
          (suggested ? '<span class="rq__badge-suggest">For your room</span>' : '') +
          '<span class="rq__tile-art">' + art + '</span>' +
          '<span class="rq__tile-name">' + esc(s.id) + '</span>' +
          (s.blurb ? '<span class="rq__tile-blurb">' + esc(s.blurb) + '</span>' : '') +
          '</button>';
      }).join('') +
      '</div>';
  };

  App.prototype.renderColorsStep = function () {
    var self = this;
    var custom = this.answers.colors.filter(function (c) { return !colorHex(c); });
    var picked = this.answers.colors.length;
    var atCap = picked >= MAX_COLORS;

    // Colours suited to the chosen style come first under their own heading;
    // the rest stay visible below. Re-ordering rather than filtering — a
    // swatch that vanishes when you go back a step reads as a bug.
    var recommended = styleColorsFor(this.answers.style) || [];
    var groups = [];
    if (recommended.length) {
      var picks = recommended.map(colorById).filter(Boolean);
      if (picks.length) groups.push({ label: 'Popular for ' + this.answers.style, colors: picks });
    }
    COLOR_GROUPS.forEach(function (g) {
      var rest = g.colors.filter(function (c) { return recommended.indexOf(c.id) === -1; });
      if (rest.length) groups.push({ label: g.label, colors: rest });
    });

    function swatch(c) {
      var selected = self.answers.colors.indexOf(c.id) !== -1;
      // At the cap, unselected swatches are genuinely disabled — the old
      // behaviour swallowed the tap with no feedback at all.
      var blocked = atCap && !selected;
      return '<button type="button" class="rq__swatch' + (selected ? ' is-selected' : '') + '"' +
        ' aria-pressed="' + selected + '"' + (blocked ? ' disabled' : '') +
        ' data-action="toggle-color" data-value="' + esc(c.id) + '">' +
        '<span class="rq__swatch-chip" style="background:' + c.hex + '">' +
        (selected ? '<span class="rq__swatch-tick" aria-hidden="true">✓</span>' : '') +
        '</span>' +
        '<span class="rq__swatch-name">' + esc(c.id) + '</span>' +
        '</button>';
    }

    return (this.prefilledFromPhoto && this.answers.roomPhoto
      ? '<p class="rq__microcopy rq__microcopy--note">✓ Pre-picked from your room photo — tweak as you like.</p>'
      : '') +
      '<p class="rq__color-count' + (atCap ? ' is-full' : '') + '" role="status">' +
      (picked ? picked + ' of ' + MAX_COLORS + ' chosen — your first pick leads the design.' +
        (atCap ? ' Deselect one to swap.' : '') : 'Two to four colors usually weave best.') +
      '</p>' +
      groups.map(function (g, gi) {
        var id = 'rq-cgroup-' + gi;
        return '<section class="rq__color-group" role="group" aria-labelledby="' + id + '">' +
          '<h3 class="rq__color-group-label" id="' + id + '">' + esc(g.label) + '</h3>' +
          '<div class="rq__grid rq__grid--colors">' + g.colors.map(swatch).join('') + '</div>' +
          '</section>';
      }).join('') +
      '<div class="rq__custom-color">' +
      custom.map(function (c) {
        return '<span class="rq__chip">' + esc(c) + '<button type="button" data-action="toggle-color" data-value="' + esc(c) + '" aria-label="Remove ' + esc(c) + '">&times;</button></span>';
      }).join('') +
      '<input class="rq__input rq__input--inline" data-input="custom-color" type="text" placeholder="Add your own — e.g. dusty rose" maxlength="60">' +
      '<button class="rq__btn rq__btn--ghost" data-action="add-custom-color" type="button">Add</button>' +
      '</div>' +
      this.renderContinue(this.answers.colors.length > 0, this.answers.colors.length ? 'Continue with ' + this.answers.colors.length + (this.answers.colors.length === 1 ? ' color' : ' colors') : 'Pick at least one color');
  };

  App.prototype.renderShapeStep = function () {
    var self = this;
    var shapes = [
      { id: 'rectangular', label: 'Rectangular', art: '<svg viewBox="0 0 80 56"><rect x="8" y="8" width="64" height="40" rx="3" fill="none" stroke="currentColor" stroke-width="3"/><rect x="16" y="16" width="48" height="24" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>' },
      { id: 'round', label: 'Round', art: '<svg viewBox="0 0 80 56"><circle cx="40" cy="28" r="22" fill="none" stroke="currentColor" stroke-width="3"/><circle cx="40" cy="28" r="14" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>' },
      { id: 'oval', label: 'Oval', art: '<svg viewBox="0 0 80 56"><ellipse cx="40" cy="28" rx="31" ry="20" fill="none" stroke="currentColor" stroke-width="3"/><ellipse cx="40" cy="28" rx="21" ry="12" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>' },
      { id: 'irregular', label: 'Irregular', art: '<svg viewBox="0 0 80 56"><path d="M14 8 H66 V28 C66 33 68 36 66 40 C64 44 61 42 61 37 C61 33 59 33 58 37 C57 42 56 47 52 47 C48 47 49 41 49 37 C49 33 47 33 46 36 C45 40 44 44 40 44 C36 44 37 39 37 35 C37 31 35 31 34 35 C33 40 32 46 28 46 C24 46 25 40 25 36 C25 32 23 32 22 35 C21 38 19 41 16 39 C13 37 14 32 14 28 Z" fill="none" stroke="currentColor" stroke-width="3" stroke-linejoin="round"/><path d="M20 14 H60 M22 21 C28 25 34 19 40 23 C46 27 52 21 58 24" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>' },
      { id: 'animal', label: 'Animal shape', art: '<svg viewBox="0 0 80 56"><path d="M18 20 C19 15 22 12 24 12 C26 12 27 15 28 18 C33 16 41 16 46 18 C47 14 49 11 51 11 C53 11 55 14 55 18 C57 19 59 21 60 23 C63 19 67 16 71 17 C74 18 74 21 71 22 C68 23 65 24 62 27 C62 29 61 31 60 33 C59 35 57 37 55 38 C55 42 53 45 51 45 C49 45 47 42 46 38 C41 40 33 40 28 38 C27 42 25 45 23 45 C21 45 19 41 18 36 C15 36 12 35 10 33 C7 31 7 25 10 23 C12 21 15 20 18 20 Z" fill="none" stroke="currentColor" stroke-width="3" stroke-linejoin="round"/><path d="M33 21 C34 24 34 30 33 35 M40 20 C41 24 41 30 40 36 M47 21 C48 24 48 30 47 35" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>' }
    ];
    return '<div class="rq__grid rq__grid--shapes" role="radiogroup" aria-label="Rug shape">' +
      shapes.map(function (s) {
        var selected = self.answers.shape === s.id;
        return '<button type="button" class="rq__tile rq__tile--shape' + (selected ? ' is-selected' : '') + '" role="radio" aria-checked="' + selected + '" data-action="pick-shape" data-value="' + s.id + '">' +
          '<span class="rq__shape-art">' + s.art + '</span>' +
          '<span class="rq__tile-name">' + s.label + '</span>' +
          '</button>';
      }).join('') +
      '</div>' +
      this.renderContinue(!!this.answers.shape, this.answers.shape ? 'Continue' : 'Pick a shape');
  };

  App.prototype.renderSizeStep = function () {
    var self = this;
    var shape = this.effShape();
    var presets = SIZE_PRESETS[shape] || SIZE_PRESETS.rectangular;
    var isCustom = this.answers.size === '__custom__';
    var customOk = isCustom && customSizeOk(this.answers.customSize, shape);
    // A size carried over from the other flow (e.g. a round preset picked in
    // the quiz before switching to upload) may not exist among these presets —
    // require an explicit re-pick instead of silently accepting it.
    var sizeKnown = isCustom || presets.indexOf(this.answers.size) !== -1;
    return '<div class="rq__grid rq__grid--sizes" role="radiogroup" aria-label="Rug size">' +
      presets.map(function (s) {
        var selected = self.answers.size === s;
        var label = shape === 'round' ? s.split('x')[0].trim() + ' ft across' : s;
        return '<button type="button" class="rq__pill' + (selected ? ' is-selected' : '') + '" role="radio" aria-checked="' + selected + '" data-action="pick-size" data-value="' + esc(s) + '">' + esc(label) + '</button>';
      }).join('') +
      '<button type="button" class="rq__pill' + (isCustom ? ' is-selected' : '') + '" role="radio" aria-checked="' + isCustom + '" data-action="pick-size" data-value="__custom__">Custom size</button>' +
      '</div>' +
      (shape === 'irregular' || shape === 'animal'
        ? '<p class="rq__microcopy">Sizes are the approximate footprint at the widest points.</p>'
        : '') +
      (isCustom
        ? '<div class="rq__custom-size"><input class="rq__input" data-input="custom-size" type="text" inputmode="text" placeholder="' + customSizePlaceholder(shape) + '" value="' + esc(this.answers.customSize) + '" autocomplete="off">' +
          '<p class="rq__microcopy">' + (shape === 'round' ? 'Minimum 2 feet (61 cm) across.' : 'Minimum 2x2 feet (61x61 cm).') + ' Feet or centimeters both work.</p></div>'
        : '') +
      (function (self) {
        var q = self.designQuality();
        return q ? '<p class="rq__quality rq__quality--' + q.level + '">' + esc(q.text) + '</p>' : '';
      })(this) +
      this.renderContinue(!!this.answers.size && sizeKnown && (!isCustom || !!customOk),
        this.answers.size && !sizeKnown ? 'Pick a size' : (isCustom && !customOk ? customSizeError(this.answers.customSize, shape) : 'Continue'));
  };

  App.prototype.renderMaterialStep = function () {
    var self = this;
    var size = this.sizeValue();
    var style = this.answers.style;

    // Materials the chosen style actually weaves well come first. Same rule as
    // the colour step: re-order and explain, never hide — a shopper can still
    // buy a plain jute rug even if they clicked a heritage tile.
    var suited = [], others = [];
    MATERIALS.forEach(function (m) {
      (materialFitFor(m.id, style).recommended ? suited : others).push(m);
    });
    var groups = suited.length && style
      ? [{ label: 'Best for ' + style, items: suited }, { label: 'Also available', items: others }]
      : [{ label: null, items: MATERIALS }];

    function row(m) {
        var selected = self.answers.material === m.id;
        // Rate is size-dependent, so quote the rate for THIS rug rather than
        // a headline number the shopper won't actually pay. Total first, rate
        // second: naming the size here would invite width x height arithmetic,
        // which is wrong for a round rug priced on its circle area.
        var est = priceFor(m.id, size, self.effShape());
        var effRate = effectiveRateFor(m.id, size, self.effShape());
        var cur = PRICING && PRICING.currency;
        var priceLine = est != null && effRate != null
          ? fmtMoney(est, cur) + ' · ' + fmtMoney(effRate, cur) + ' per sq ft'
          : '';
        var warning = materialFitFor(m.id, style).warning;
        return '<button type="button" class="rq__row' + (selected ? ' is-selected' : '') + '" role="radio" aria-checked="' + selected + '" data-action="pick-material" data-value="' + esc(m.id) + '">' +
          '<span class="rq__row-main"><span class="rq__row-name">' + esc(m.id) + '</span>' +
          '<span class="rq__row-blurb">' + esc(m.blurb) + '</span>' +
          (priceLine ? '<span class="rq__row-price">' + priceLine + '</span>' : '') +
          (warning ? '<span class="rq__row-note">' + esc(warning) + '</span>' : '') +
          '</span>' +
          (m.tag ? '<span class="rq__tag">' + esc(m.tag) + '</span>' : '') +
          '</button>';
    }

    return groups.map(function (g) {
      return (g.label ? '<h3 class="rq__color-group-label">' + esc(g.label) + '</h3>' : '') +
        '<div class="rq__list" role="radiogroup" aria-label="' + esc(g.label || 'Material') + '">' +
        g.items.map(row).join('') +
        '</div>';
    }).join('');
  };

  App.prototype.renderDetailsStep = function () {
    var suggestions = ['Medallion centerpiece', 'Floral border', 'Kid & pet friendly feel', 'Match a green sofa', 'Subtle, low-contrast pattern'];
    return '<textarea class="rq__textarea" data-input="description" maxlength="500" rows="4" placeholder="e.g. medallion pattern with a floral border, colors that pick up my green sofa…">' + esc(this.answers.description) + '</textarea>' +
      '<div class="rq__suggestions">' +
      suggestions.map(function (s) {
        return '<button type="button" class="rq__chip rq__chip--add" data-action="add-suggestion" data-value="' + esc(s) + '">+ ' + esc(s) + '</button>';
      }).join('') +
      '</div>' +
      this.stepActions(
        '<button class="rq__btn rq__btn--ghost" data-action="skip-details" type="button">Skip</button>' +
        '<button class="rq__btn rq__btn--primary" data-action="continue" type="button">Continue</button>'
      );
  };

  App.prototype.renderEmailStep = function () {
    if (this.uploadMode) return this.renderUploadEmailStep();
    var summary = [this.answers.style, this.sizeValue(), this.answers.shape, this.answers.material]
      .filter(Boolean).map(esc).join(' · ');
    return '<div class="rq__recap">' + summary + '<br><span class="rq__recap-colors">' +
      this.answers.colors.map(function (c) { return '<span class="rq__dot" style="background:' + guessHex(c) + '" title="' + esc(c) + '"></span>'; }).join('') +
      '</span></div>' +
      (this._emailEstimateHTML()) +
      '<input class="rq__input" data-input="email" type="email" placeholder="you@example.com" value="' + esc(this.answers.email) + '" autocomplete="email" required>' +
      '<p class="rq__microcopy">Up to 5 free design sessions per email · 4 unique designs each · no spam, ever.</p>' +
      (this.config.customerId
        ? '<p class="rq__microcopy">✓ Logged in — your designs will be saved to your account on every device.</p>'
        : (this.config.proxyPath ? '<p class="rq__microcopy">Have an account? <a class="rq__link" href="/account/login?return_url=/pages/design-your-rug">Log in</a> to keep your designs on every device — optional.' +
            (this.answers.roomPhoto ? ' Heads up: you’ll re-add your room photo after logging in.' : '') + '</p>' : '')) +
      this.stepActions(
        '<button class="rq__btn rq__btn--primary rq__btn--wide" data-action="generate" type="button">Weave my 4 designs →</button>'
      );
  };

  App.prototype.renderUploadEmailStep = function () {
    var summary = ['Your design', this.sizeValue(), this.answers.material]
      .filter(Boolean).map(esc).join(' · ');
    var quality = this.designQuality();
    return '<div class="rq__recap">' +
      (this.answers.designImage ? '<span class="rq__recap-thumb"><img src="' + this.answers.designImage + '" alt="Your rug design"></span>' : '') +
      summary + '</div>' +
      (this._emailEstimateHTML()) +
      (quality ? '<p class="rq__quality rq__quality--' + quality.level + '">' + esc(quality.text) + '</p>' : '') +
      '<input class="rq__input" data-input="email" type="email" placeholder="you@example.com" value="' + esc(this.answers.email) + '" autocomplete="email" required>' +
      '<label class="rq__rights"><input type="checkbox" data-input="rights"> I confirm I own the rights to use this design, and understand a final proof will be sent for my approval before weaving.</label>' +
      (this.config.customerId
        ? '<p class="rq__microcopy">✓ Logged in — this design will be saved to your account.</p>'
        : (this.config.proxyPath ? '<p class="rq__microcopy">Have an account? <a class="rq__link" href="/account/login?return_url=/pages/design-your-rug">Log in</a> to keep your designs on every device — optional.</p>' : '')) +
      this.stepActions(
        '<button class="rq__btn rq__btn--primary rq__btn--wide" data-action="generate" type="button">Review my rug →</button>'
      );
  };

  // Price preview on the final step: designing is free, so show what the
  // chosen size + material would cost before the shopper commits an email.
  App.prototype._emailEstimateHTML = function () {
    var est = priceFor(this.answers.material, this.sizeValue(), this.effShape());
    if (est == null) return '';
    return '<p class="rq__estimate">Estimated price: <strong>' + fmtMoney(est) + '</strong> — designing is free, and nothing is woven until you approve.</p>';
  };

  // Every quiz step's actions live in one sticky bar pinned to the bottom of
  // the viewport, so Back and Continue stay reachable on a long step (20
  // colour swatches, 7 materials) without scrolling to the end of the list.
  App.prototype.stepActions = function (buttonsHTML) {
    var back = this.stepIndex > 0
      ? '<button class="rq__btn rq__btn--ghost rq__actions-back" data-action="back" type="button" aria-label="Previous question">&larr; Back</button>'
      : '';
    return '<div class="rq__actions rq__actions--bar">' + back + buttonsHTML + '</div>';
  };

  App.prototype.renderContinue = function (enabled, label) {
    return this.stepActions(
      '<button class="rq__btn rq__btn--primary" data-action="continue" type="button"' + (enabled ? '' : ' disabled') + '>' + esc(label || 'Continue') + '</button>'
    );
  };

  App.prototype.renderGenerating = function () {
    if (this.uploadMode) {
      return '<div class="rq__generating">' +
        '<div class="rq__loom" aria-hidden="true"><span></span><span></span><span></span><span></span></div>' +
        '<h2 class="rq__title">Preparing your rug</h2>' +
        '<p class="rq__hint">Uploading your design and pricing your size…</p>' +
        '</div>';
    }
    return '<div class="rq__generating">' +
      '<div class="rq__loom" aria-hidden="true"><span></span><span></span><span></span><span></span></div>' +
      '<h2 class="rq__title">Weaving your designs</h2>' +
      '<p class="rq__hint" data-generating-line aria-live="polite">' + GENERATING_LINES[0] + '</p>' +
      '<div class="rq__skeletons">' +
      '<span class="rq__skeleton"></span><span class="rq__skeleton"></span><span class="rq__skeleton"></span><span class="rq__skeleton"></span>' +
      '</div>' +
      '<p class="rq__microcopy">Usually 20–40 seconds. Each design is one of a kind.</p>' +
      '</div>';
  };

  App.prototype.renderResults = function () {
    var self = this;
    var pricing = this.generation.pricing;
    var remaining = this.generation.remaining;
    var isUpload = this.generation.source === 'upload';
    return '<div class="rq__results">' +
      '<h2 class="rq__title">' + (isUpload ? 'Your design, ready to weave' : 'Your rug, four ways') + '</h2>' +
      (isUpload
        ? '<p class="rq__hint">Your design · ' + esc(this.sizeValue()) + ' · ' + esc(this.answers.material) + '. We weave it exactly as you sent it — you approve the final proof before anything is made.</p>'
        : '<p class="rq__hint">' + esc(this.answers.style) + ' · ' + esc(this.sizeValue()) + ' · ' + esc(this.answers.material) + '. These designs are yours — we’ll never sell them to anyone else.</p>') +
      '<div class="rq__grid rq__grid--results" role="radiogroup" aria-label="Choose your design">' +
      this.generation.images.map(function (img, i) {
        var selected = self.selectedIndex === i;
        var comp = self.composites[i];
        var showRoom = self.cardView[i] === 'room' && comp && comp.status === 'done';
        var media;
        if (showRoom) {
          media = '<img src="' + comp.src + '" alt="Design ' + (i + 1) + ' shown in your room" class="rq__img--room">';
        } else {
          media = '<img src="' + img.src + '" alt="Generated rug design ' + (i + 1) + '"' + (self.basisShape() === 'round' ? ' class="rq__img--round"' : '') + '>';
        }
        if (comp && comp.status === 'loading') {
          media += '<span class="rq__media-loading" aria-live="polite"><span class="rq__spinner"></span>Placing in your room…</span>';
        }
        return '<div class="rq__result' + (selected ? ' is-selected' : '') + '">' +
          '<button type="button" class="rq__result-pick" role="radio" aria-checked="' + selected + '" data-action="select-design" data-index="' + i + '" aria-label="Select design ' + (i + 1) + '">' +
          '<span class="rq__result-media">' + media + '</span>' +
          '<span class="rq__result-label">' + (img.name ? esc(img.name) : (isUpload ? 'Your design' : 'Design ' + (i + 1))) + '</span>' +
          '</button>' +
          (self.answers.roomPhoto
            ? '<div class="rq__view-toggle" role="group" aria-label="View design ' + (i + 1) + ' as">' +
              '<button type="button" class="rq__view-btn' + (!showRoom ? ' is-active' : '') + '" data-action="view-rug" data-index="' + i + '">Rug</button>' +
              '<button type="button" class="rq__view-btn' + (showRoom ? ' is-active' : '') + '" data-action="view-room" data-index="' + i + '">In your room</button>' +
              '</div>' +
              (comp && comp.status === 'error' ? '<p class="rq__microcopy rq__microcopy--error">Room preview failed — tap “In your room” to retry.</p>' : '')
            : '') +
          '<button type="button" class="rq__fav' + (img.isFavorite ? ' is-active' : '') + '" data-action="favorite" data-index="' + i + '" aria-pressed="' + img.isFavorite + '" aria-label="Save design ' + (i + 1) + '">' +
          '<svg viewBox="0 0 24 24" width="18" height="18"><path d="M12 21s-7.5-4.9-9.8-9.2C.6 8.6 2.3 5 5.7 5c2 0 3.4 1.1 4.3 2.6h4c.9-1.5 2.3-2.6 4.3-2.6 3.4 0 5.1 3.6 3.5 6.8C19.5 16.1 12 21 12 21z" fill="' + (img.isFavorite ? 'currentColor' : 'none') + '" stroke="currentColor" stroke-width="1.6"/></svg>' +
          '</button>' +
          '</div>';
      }).join('') +
      '</div>' +
      '<div class="rq__results-secondary">' +
      '<button class="rq__btn rq__btn--ghost" data-action="edit-answers" type="button">' + (isUpload ? 'Edit details' : 'Edit my answers') + '</button>' +
      (isUpload
        ? '<button class="rq__btn rq__btn--ghost" data-action="remove-design" type="button">Upload a different image</button>'
        : '<button class="rq__btn rq__btn--ghost" data-action="regenerate" type="button"' + (remaining === 0 ? ' disabled' : '') + '>' +
          'Weave 4 more' + (remaining != null ? ' (' + remaining + ' left)' : '') + '</button>') +
      (wishlistAll().length ? '<button class="rq__btn rq__btn--ghost" data-action="open-wishlist" type="button">♥ Saved (' + wishlistAll().length + ')</button>' : '') +
      '</div>' +
      (isUpload && !this.answers.roomPhoto
        ? '<p class="rq__microcopy rq__add-room"><button type="button" class="rq__link" data-action="add-room-photo">Add a room photo to see it in your space</button>' +
          '<input type="file" accept="image/*" data-input="room-photo-results" hidden></p>'
        : '') +
      (isUpload
        ? ''
        : '<p class="rq__microcopy rq__upload-entry rq__upload-entry--results">Have your own artwork? <button type="button" class="rq__link" data-action="enter-upload">Order your own design →</button></p>') +
      this.renderSizePanel() +
      '<div class="rq__checkout-bar' + (this.selectedIndex != null ? ' is-visible' : '') + '">' +
      '<div class="rq__checkout-info">' +
      (this.selectedIndex != null ? '<strong>' + esc((this.generation.images[this.selectedIndex] || {}).name || (isUpload ? 'Your design' : 'Design ' + (this.selectedIndex + 1))) + '</strong> · ' + esc(this.orderSize || this.sizeValue()) : 'Select a design above') +
      (this.effectivePrice() != null
        ? '<span class="rq__price">' + fmtMoney(this.effectivePrice(), pricing && pricing.currency) + (this.orderSize ? ' <small>est.</small>' : '') + '</span>'
        : '<span class="rq__price rq__price--tbd">Price at checkout</span>') +
      '</div>' +
      '<button class="rq__btn rq__btn--ghost" data-action="toggle-size-edit" type="button"' + (this.selectedIndex == null ? ' disabled' : '') + '>Change size</button>' +
      '<button class="rq__btn rq__btn--primary" data-action="checkout" type="button"' + (this.selectedIndex == null ? ' disabled' : '') + '>Add to cart</button>' +
      '</div>' +
      '</div>';
  };

  // The material and shape this generation was actually made with. The quiz
  // answers can drift from it — edit answers, come back to the results via
  // "Back to my 4 designs", or restore a session — and checkout always
  // re-prices from the generation, so pricing must follow the generation too.
  // Shape to use on the results screen: the generation's own, falling back to
  // the quiz answer before a generation exists.
  App.prototype.basisShape = function () {
    var basis = this.genPricingBasis();
    return (basis && basis.shape) || this.effShape();
  };

  App.prototype.genPricingBasis = function () {
    var pricing = this.generation && this.generation.pricing;
    if (!pricing) return null;
    return {
      material: pricing.material || this.answers.material,
      shape: pricing.shape || this.effShape()
    };
  };

  // Price for the size the user will actually order (client estimate when
  // the size was changed; checkout returns the authoritative number). When
  // the rate table is unavailable this returns null — showing the ORIGINAL
  // size's price for a different size would misquote the sale.
  App.prototype.effectivePrice = function () {
    var pricing = this.generation && this.generation.pricing;
    if (!pricing) return null;
    if (!this.orderSize) return pricing.price;
    var basis = this.genPricingBasis();
    return priceFor(basis.material, this.orderSize, basis.shape);
  };

  App.prototype.renderSizePanel = function () {
    if (!this.sizeEditOpen) return '';
    var self = this;
    var original = this.sizeValue();
    var basisShape = this.basisShape();
    var options = [original];
    (SIZE_PRESETS[basisShape] || SIZE_PRESETS.rectangular).forEach(function (s) {
      if (options.indexOf(s) === -1) options.push(s);
    });
    var current = this.orderSize || original;
    var isCustom = options.indexOf(current) === -1;
    return '<div class="rq__size-panel" role="radiogroup" aria-label="Order size">' +
      '<p class="rq__microcopy">Order this design in a different size — priced per square foot.</p>' +
      options.map(function (s) {
        var on = s === current;
        var label = s === original ? s + ' (original)' : s;
        return '<button type="button" class="rq__pill rq__pill--sm' + (on ? ' is-selected' : '') + '" role="radio" aria-checked="' + on + '" data-action="order-size" data-value="' + esc(s) + '">' + esc(label) + '</button>';
      }).join('') +
      '<button type="button" class="rq__pill rq__pill--sm' + (isCustom || this.sizeCustomOpen ? ' is-selected' : '') + '" data-action="order-size-custom">' + (isCustom ? esc(current) : 'Custom size') + '</button>' +
      (this.sizeCustomOpen
        ? '<span class="rq__custom-size rq__custom-size--inline">' +
          '<input class="rq__input rq__input--sm" data-input="order-size-custom" type="text" inputmode="text" placeholder="' + customSizePlaceholder(this.basisShape()) + '" value="' + esc(this._sizeCustomErr ? (this._sizeCustomVal || '') : (isCustom ? current : '')) + '" autocomplete="off">' +
          '<button type="button" class="rq__btn rq__btn--primary rq__btn--small" data-action="order-size-apply">Apply</button>' +
          (this._sizeCustomErr ? '<span class="rq__microcopy rq__microcopy--error">' + esc(this._sizeCustomErr) + '</span>' : '') +
          '</span>'
        : '') +
      '</div>';
  };

  /* ---------- event wiring ---------- */

  App.prototype.bind = function () {
    var self = this;

    this.root.querySelectorAll('[data-action]').forEach(function (el) {
      el.addEventListener('click', function (ev) {
        self.handleAction(el.getAttribute('data-action'), el, ev);
      });
    });

    var designInput = this.root.querySelector('[data-input="design-file"]');
    var designZone = this.root.querySelector('[data-design-dropzone]');
    if (designInput && designZone) {
      designInput.addEventListener('change', function () {
        self.handleDesignFile(designInput.files && designInput.files[0]);
      });
      designZone.addEventListener('click', function () { designInput.click(); });
      designZone.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); designInput.click(); }
      });
    }

    // Upload-results: optional room photo for the in-your-space preview.
    var roomResultsInput = this.root.querySelector('[data-input="room-photo-results"]');
    if (roomResultsInput) {
      roomResultsInput.addEventListener('change', function () {
        var file = roomResultsInput.files && roomResultsInput.files[0];
        if (!file) return;
        analyzeRoomPhoto(file).then(function (res) {
          self.answers.roomPhoto = res.dataUrl;
          self.render();
          self.placeDesignInRoom(0);
          track('upload_room_added', {});
        }).catch(function () { /* keep the results view as-is */ });
      });
    }

    var photoInput = this.root.querySelector('[data-input="photo"]');
    var dropzone = this.root.querySelector('[data-dropzone]');
    if (photoInput && dropzone) {
      photoInput.addEventListener('change', function () {
        self.handlePhotoFile(photoInput.files && photoInput.files[0]);
      });
      dropzone.addEventListener('click', function () { photoInput.click(); });
      dropzone.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); photoInput.click(); }
      });
      dropzone.addEventListener('dragover', function (ev) {
        ev.preventDefault();
        dropzone.classList.add('is-dragover');
      });
      dropzone.addEventListener('dragleave', function () { dropzone.classList.remove('is-dragover'); });
      dropzone.addEventListener('drop', function (ev) {
        ev.preventDefault();
        dropzone.classList.remove('is-dragover');
        var file = ev.dataTransfer && ev.dataTransfer.files && ev.dataTransfer.files[0];
        self.handlePhotoFile(file);
      });
    }

    var customColor = this.root.querySelector('[data-input="custom-color"]');
    if (customColor) {
      customColor.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter') { ev.preventDefault(); self.addCustomColor(); }
      });
    }

    var customSize = this.root.querySelector('[data-input="custom-size"]');
    if (customSize) {
      customSize.addEventListener('input', function () {
        self.answers.customSize = customSize.value;
        var btn = self.root.querySelector('[data-action="continue"]');
        var ok = customSizeOk(customSize.value, self.effShape());
        if (btn) {
          btn.disabled = !ok;
          btn.textContent = ok ? 'Continue' : customSizeError(customSize.value, self.effShape());
        }
      });
      customSize.focus();
    }

    var description = this.root.querySelector('[data-input="description"]');
    if (description) {
      description.addEventListener('input', function () { self.answers.description = description.value; });
    }

    var email = this.root.querySelector('[data-input="email"]');
    if (email) {
      email.addEventListener('input', function () { self.answers.email = email.value; });
      email.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter') { ev.preventDefault(); self.handleAction('generate'); }
      });
    }

    if (this.phase === 'generating') {
      var lineEl = this.root.querySelector('[data-generating-line]');
      var lineIdx = 0;
      this._lineTimer = setInterval(function () {
        lineIdx = (lineIdx + 1) % GENERATING_LINES.length;
        if (lineEl) lineEl.textContent = GENERATING_LINES[lineIdx];
      }, 3200);
    }
  };

  App.prototype.addCustomColor = function () {
    var input = this.root.querySelector('[data-input="custom-color"]');
    var value = input && input.value.trim().toLowerCase();
    if (!value) return;
    if (this.answers.colors.length >= MAX_COLORS) return;
    if (this.answers.colors.indexOf(value) === -1) this.answers.colors.push(value);
    this._persist();
    this.render();
  };

  App.prototype.handleAction = function (action, el) {
    var self = this;
    var value = el ? el.getAttribute('data-value') : null;
    var index = el ? parseInt(el.getAttribute('data-index'), 10) : NaN;

    if (!this._wlSel) this._wlSel = {};
    if (handleWishlistAction(this, action, el)) return;

    switch (action) {
      case 'back':
        this.goTo(this.stepIndex - 1);
        break;
      case 'skip-photo':
        track('photo_skipped', {});
        this.next();
        break;
      case 'remove-photo':
        this.answers.roomPhoto = null;
        this.roomPalette = null;
        this.photoError = null;
        this.render();
        break;
      case 'view-rug':
        this.cardView[index] = 'rug';
        this.render();
        break;
      case 'view-room':
        this.placeDesignInRoom(index);
        break;
      case 'continue':
        this.next();
        break;
      case 'pick-style':
        this.answers.style = value;
        this.render();
        setTimeout(function () { self.next(); }, 220);
        break;
      case 'toggle-color': {
        var at = this.answers.colors.indexOf(value);
        if (at !== -1) this.answers.colors.splice(at, 1);
        else if (this.answers.colors.length < MAX_COLORS) this.answers.colors.push(value);
        this._persist();
        this.render();
        break;
      }
      case 'add-custom-color':
        this.addCustomColor();
        break;
      case 'pick-shape':
        this.answers.shape = value;
        if (this.answers.size && this.answers.size !== '__custom__' &&
            (SIZE_PRESETS[value] || []).indexOf(this.answers.size) === -1) {
          this.answers.size = null;
        }
        this._persist();
        this.render();
        break;
      case 'pick-size':
        this.answers.size = value;
        this.render();
        if (value !== '__custom__') setTimeout(function () { self.next(); }, 220);
        break;
      case 'pick-material':
        this.answers.material = value;
        this.render();
        setTimeout(function () { self.next(); }, 220);
        break;
      case 'add-suggestion': {
        var text = this.answers.description.trim();
        this.answers.description = text ? text.replace(/[.\s]*$/, '') + '. ' + value : value;
        this._persist();
        this.render();
        break;
      }
      case 'skip-details':
        this.answers.description = '';
        this.next();
        break;
      case 'generate': {
        var emailValue = this.answers.email.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
          this.error = 'Please enter a valid email so we can save your designs.';
          this.render();
          return;
        }
        this.error = null;
        this._persist();
        track('email_captured', {});
        this.startGeneration();
        break;
      }
      case 'select-design':
        this.selectedIndex = index;
        this.render();
        this._persistGeneration();
        track('design_selected', { index: index });
        break;
      case 'show-results':
        // Re-enter the results under the generation's own flow so shape-aware
        // rendering/pricing stays correct after a detour into the other mode.
        if (this.generation) this.uploadMode = this.generation.source === 'upload';
        this.phase = 'results';
        this.error = null;
        this.render();
        track('results_reopened', {});
        break;
      case 'favorite': {
        var img = this.generation.images[index];
        img.isFavorite = !img.isFavorite;
        var wishId = this.generation.generationId + '-' + index;
        if (img.isFavorite) {
          var p = this.generation.pricing || {};
          wishlistAdd({
            id: wishId,
            generationId: this.generation.generationId,
            index: index,
            src: img.src,
            name: img.name || '',
            style: this.answers.style,
            size: this.sizeValue(),
            material: (this.genPricingBasis() || {}).material || this.answers.material,
            shape: this.basisShape(),
            price: p.price != null ? p.price : null,
            currency: p.currency,
            savedAt: Date.now()
          });
        } else {
          wishlistRemove(wishId);
        }
        track(img.isFavorite ? 'design_saved' : 'design_unsaved', { index: index });
        this.render();
        this._persistGeneration();
        // Local-first: the heart reflects the on-device wishlist, which
        // already succeeded. Server sync is best-effort — don't flip the
        // heart back on a network blip.
        this.api.favorite(this.generation.generationId, index, img.isFavorite).catch(function () {});
        break;
      }
      case 'open-wishlist':
        track('wishlist_view', { count: wishlistAll().length, page: 'quiz' });
        this._wishlistReturn = this.phase;
        this.phase = 'wishlist';
        this.render();
        break;
      case 'close-wishlist':
        this.phase = this._wishlistReturn === 'results' && this.generation ? 'results' : 'quiz';
        this._wishlistReturn = null;
        this.render();
        break;
      case 'wishlist-remove': {
        var removeId = el.getAttribute('data-id');
        wishlistRemove(removeId);
        // Keep the results-page heart in sync when the removed item belongs
        // to the generation currently on screen.
        if (this.generation) {
          var m = removeId.match(/-(\d+)$/);
          var rmIndex = m ? parseInt(m[1], 10) : -1;
          if (removeId === this.generation.generationId + '-' + rmIndex && this.generation.images[rmIndex]) {
            this.generation.images[rmIndex].isFavorite = false;
            this.api.favorite(this.generation.generationId, rmIndex, false).catch(function () {});
          }
        }
        this.render();
        break;
      }
      case 'regenerate':
        this.startGeneration();
        break;
      case 'edit-answers':
        this.goTo(0);
        break;
      case 'enter-upload':
        this.uploadMode = true;
        this.phase = 'quiz';
        this.stepIndex = 0;
        this.error = null;
        this.photoError = null;
        this._persist();
        this.render();
        track('upload_mode_entered', {});
        break;
      case 'exit-upload':
        this.uploadMode = false;
        this.phase = 'quiz';
        this.stepIndex = 0;
        this.error = null;
        this.photoError = null;
        this._persist();
        this.render();
        break;
      case 'remove-design':
        this.answers.designImage = null;
        this.designPx = null;
        this.photoError = null;
        this.uploadMode = true;
        this.phase = 'quiz';
        this.stepIndex = 0;
        this.render();
        break;
      case 'add-room-photo': {
        var roomInput = this.root.querySelector('[data-input="room-photo-results"]');
        if (roomInput) roomInput.click();
        break;
      }
      case 'checkout':
        this.doCheckout();
        break;
      case 'toggle-size-edit':
        this.sizeEditOpen = !this.sizeEditOpen;
        this.sizeCustomOpen = false;
        this._sizeCustomErr = null;
        this._sizeCustomVal = null;
        this.render();
        break;
      case 'order-size':
        this.orderSize = value === this.sizeValue() ? null : value;
        this.sizeEditOpen = false;
        this.sizeCustomOpen = false;
        this._sizeCustomErr = null;
        this._sizeCustomVal = null;
        this.render();
        break;
      case 'order-size-custom':
        this.sizeCustomOpen = !this.sizeCustomOpen;
        this._sizeCustomErr = null;
        this._sizeCustomVal = null;
        this.render();
        break;
      case 'order-size-apply': {
        var sizeInput = this.root.querySelector('[data-input="order-size-custom"]');
        var raw = sizeInput ? sizeInput.value.trim() : '';
        if (!customSizeOk(raw, this.basisShape())) {
          this._sizeCustomErr = customSizeError(raw, this.basisShape());
          this._sizeCustomVal = raw;
          this.render();
          break;
        }
        this.orderSize = raw === this.sizeValue() ? null : raw;
        this.sizeEditOpen = false;
        this.sizeCustomOpen = false;
        this._sizeCustomErr = null;
        this._sizeCustomVal = null;
        this.render();
        break;
      }
      case 'close-dialog': {
        var dialog = this.root.querySelector('[data-demo-dialog]');
        if (dialog) dialog.close();
        break;
      }
    }
  };

  /* ---------- standalone saved-designs page ---------- */

  function WishlistPage(root, config) {
    var self = this;
    this.root = root;
    this.config = config || {};
    this._wlSel = {};
    identify(this.config);
    track('wishlist_view', { count: wishlistAll().length, page: 'saved-designs' });
    this.render();
    // Saved designs are re-priced for other sizes, so this page needs the
    // rate table too.
    if (!PRICING && (this.config.apiBaseUrl || this.config.proxyPath)) {
      new HttpRugApi(this.config.apiBaseUrl, this.config.proxyPath).options()
        .then(function (body) { if (applyPricingConfig(body && body.pricing)) self.render(); })
        .catch(function () { /* prices stay hidden until checkout */ });
    }
    wishlistSyncAccount(this.config, function () { self.render(); });
  }

  WishlistPage.prototype.render = function () {
    var self = this;
    var items = wishlistAll();
    var quizUrl = this.config.quizUrl || '/pages/design-your-rug';
    this.root.innerHTML =
      '<div class="rq rq--wishlist-page"><div class="rq__wishlist">' +
      wishlistLoginNote(this.config) +
      (items.length
        ? '<p class="rq__hint">Pick a size and order any design, or <a class="rq__link" href="' + esc(quizUrl) + '">design a new rug</a>.</p>' +
          '<div class="rq__grid rq__grid--results">' + wishlistCardsHTML(items, this._wlSel, this._wlCustom) + '</div>'
        : '<p class="rq__hint">Nothing saved yet. Take the quiz, tap the ♥ on designs you love, and they’ll wait for you here.</p>' +
          '<div class="rq__actions"><a class="rq__btn rq__btn--primary" href="' + esc(quizUrl) + '">Design your rug</a></div>') +
      '</div>' +
      '<dialog class="rq__dialog" data-demo-dialog><div data-demo-body></div>' +
      '<button class="rq__btn rq__btn--primary" data-action="close-dialog" type="button">Close</button></dialog>' +
      '</div>';
    this.root.querySelectorAll('[data-action]').forEach(function (el) {
      el.addEventListener('click', function () {
        self.handleAction(el.getAttribute('data-action'), el);
      });
    });
  };

  WishlistPage.prototype.handleAction = function (action, el) {
    if (handleWishlistAction(this, action, el)) return;
    if (action === 'wishlist-remove') {
      wishlistRemove(el.getAttribute('data-id'));
      this.render();
    } else if (action === 'close-dialog') {
      var dialog = this.root.querySelector('[data-demo-dialog]');
      if (dialog) dialog.close();
    }
  };

  /* ---------- boot ---------- */

  function boot() {
    // Fill placeholder art slots anywhere on the page (homepage tiles etc.)
    // until real photography replaces them via theme settings.
    document.querySelectorAll('img[data-rug-art]').forEach(function (img) {
      if (img.dataset.rugArtFilled) return;
      img.dataset.rugArtFilled = '1';
      img.src = rugSVG({
        style: img.getAttribute('data-rug-art') || 'Abstract',
        seed: parseInt(img.getAttribute('data-rug-seed'), 10) || 4,
        shape: img.getAttribute('data-rug-shape') || 'rectangular',
        size: img.getAttribute('data-rug-size') || ''
      });
    });

    document.querySelectorAll('[data-rug-quiz]').forEach(function (rootEl) {
      if (rootEl.__rugQuizApp) return;
      var config = {};
      var configEl = rootEl.querySelector('[data-rug-quiz-config]');
      if (configEl) {
        try { config = JSON.parse(configEl.textContent); } catch (e) { /* fall back to defaults */ }
      }
      if (config.mock == null) config.mock = !config.apiBaseUrl;
      applyApiOverride(config);
      rootEl.__rugQuizApp = new App(rootEl, config);
    });

    document.querySelectorAll('[data-rug-wishlist]').forEach(function (rootEl) {
      if (rootEl.__rugWishlistPage) return;
      var config = {};
      var configEl = rootEl.querySelector('[data-rug-wishlist-config]');
      if (configEl) {
        try { config = JSON.parse(configEl.textContent); } catch (e) { /* defaults */ }
      }
      if (config.mock == null) config.mock = !config.apiBaseUrl;
      applyApiOverride(config);
      rootEl.__rugWishlistPage = new WishlistPage(rootEl, config);
    });
  }

  function applyApiOverride(config) {
      // Test override: ?api=live forces the real backend, ?api=demo forces mock.
      // Persisted in sessionStorage so it survives reloads within the tab;
      // ?api=default clears it. Live requires a configured API base URL.
      try {
        var apiParam = new URLSearchParams(window.location.search).get('api');
        if (apiParam === 'live' || apiParam === 'demo') sessionStorage.setItem('rq-api-mode', apiParam);
        else if (apiParam === 'default') sessionStorage.removeItem('rq-api-mode');
        var apiMode = sessionStorage.getItem('rq-api-mode');
        if (apiMode === 'live' && config.apiBaseUrl) config.mock = false;
        else if (apiMode === 'demo') config.mock = true;
      } catch (e) { /* sessionStorage unavailable — keep configured mode */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.RugQuiz = { boot: boot };
})();
